// FileUpload.js (Revised with Supabase Connection Wait)

import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Modal,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TablePagination,
  TableSortLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Collapse,
  TextField,
  Tooltip,
  Snackbar,
  IconButton,
  LinearProgress,
  Grid,
  Menu
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import CachedIcon from '@mui/icons-material/Cached';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LanguageIcon from '@mui/icons-material/Language';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LinkIcon from '@mui/icons-material/Link';
import WebIcon from '@mui/icons-material/Web';
import MapIcon from '@mui/icons-material/Map';
import SettingsIcon from '@mui/icons-material/Settings';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { visuallyHidden } from '@mui/utils';
import { AuthContext } from './AuthContext'; // Assuming you have AuthContext for user details
import PlatformImportDialog from './components/PlatformImportDialog';

// v2.2 - Modern upload interface with cascading menus + URL upload working
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function FileUpload({ handleLogout }) {
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingFile, setDeletingFile] = useState('');
  const [deleteTimer, setDeleteTimer] = useState(0);
  const [deleteTimerId, setDeleteTimerId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [webpageUrl, setWebpageUrl] = useState('');
  const [isCrawling, setIsCrawling] = useState(false);
  const [showCrawlContainer, setShowCrawlContainer] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isCleaning, setIsCleaning] = useState(false);
  
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext); // Get current user from context

  // Add state for sitemap crawling
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [isSitemapCrawling, setIsSitemapCrawling] = useState(false);

  // Add state variables for URL selection
  const [sitemapUrlHierarchy, setSitemapUrlHierarchy] = useState({});
  const [flatUrls, setFlatUrls] = useState([]);
  const [selectedUrls, setSelectedUrls] = useState([]);
  const [isLoadingSitemap, setIsLoadingSitemap] = useState(false);
  const [showUrlSelectionModal, setShowUrlSelectionModal] = useState(false);
  const [ignoreRobotsTxt, setIgnoreRobotsTxt] = useState(false); // Renamed from useJsRendering

  // --- Add state for table sorting ---
  const [order, setOrder] = useState('desc'); // 'asc' or 'desc' - CHANGED to desc to show newest first
  const [orderBy, setOrderBy] = useState('created_at'); // Default sort column - CHANGED to created_at to show newest uploads first
  // ----------------------------------

  // --- NEW STATES FOR TEAM CONTEXT MODAL ---
  const [showTeamContextModal, setShowTeamContextModal] = useState(false);
  const [uploadScope, setUploadScope] = useState('personal'); // 'personal', 'company', 'team'
  const [userCompany, setUserCompany] = useState(null); // { id, name }
  const [companyTeams, setCompanyTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(''); // Store ID of selected team
  const [showCreateTeamInModalForm, setShowCreateTeamInModalForm] = useState(false);
  const [newTeamNameInModal, setNewTeamNameInModal] = useState('');
  const [newTeamDescriptionInModal, setNewTeamDescriptionInModal] = useState('');
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingCreateTeamInModal, setLoadingCreateTeamInModal] = useState(false);
  const [modalError, setModalError] = useState('');
  const [actionToPerform, setActionToPerform] = useState(null); // 'upload' or 'crawl' or 'sitemap_crawl' or 'upload_url'
  const [fileInputRef, setFileInputRef] = useState(null); // Ref for file input
  // --- END NEW STATES ---

  // --- NEW: Upload progress tracking ---
  const [uploadProgress, setUploadProgress] = useState(null); // {total, completed, failed, processing, overall_progress, files}
  const [showProgressSnackbar, setShowProgressSnackbar] = useState(false);
  const [progressPollInterval, setProgressPollInterval] = useState(null);
  // --- END NEW ---

  // --- NEW: Validation states ---
  const [isValidating, setIsValidating] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  // --- END NEW ---

  // --- NEW: File content viewer states ---
  const [selectedFileForView, setSelectedFileForView] = useState(null);
  const [loadingFileContent, setLoadingFileContent] = useState(false);
  // --- END NEW ---

  // --- NEW: Search and filter states ---
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'crawled', 'uploaded', 'personal', 'company', or team_id
  const [isRecrawling, setIsRecrawling] = useState(false);
  const [recrawlProgress, setRecrawlProgress] = useState(0);
  const [isCheckingUpdates, setIsCheckingUpdates] = useState(false);
  const [isForceRecrawlingSelected, setIsForceRecrawlingSelected] = useState(false);
  // --- END NEW ---

  // --- NEW: Upload menu states ---
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState(null);
  const [uploadSubMenuAnchor, setUploadSubMenuAnchor] = useState(null);
  const [crawlSubMenuAnchor, setCrawlSubMenuAnchor] = useState(null);
  const [selectedMainOption, setSelectedMainOption] = useState(null); // 'upload' or 'crawl'
  const [showUrlUploadDialog, setShowUrlUploadDialog] = useState(false);
  const [fileUrlInput, setFileUrlInput] = useState('');
  const [showPlatformImportDialog, setShowPlatformImportDialog] = useState(false);
  // --- END NEW ---

  // --- NEW: Subscription-based upload constraints ---
  const [uploadConstraints, setUploadConstraints] = useState({ maxFileSizeMb: null, maxFilesPerUpload: null, limitTotalUploadMb: null, limitTotalFiles: null });
  const [uploadTotals, setUploadTotals] = useState({ usedTotalUploadMb: null, remainingTotalUploadMb: null, currentFileCount: null });
  useEffect(() => {
    const fetchUploadConstraints = async () => {
      try {
        if (!userCompany?.id) {
          setUploadConstraints({ maxFileSizeMb: null, maxFilesPerUpload: null, limitTotalUploadMb: null });
          setUploadTotals({ usedTotalUploadMb: null, remainingTotalUploadMb: null });
          return;
        }
        const { data: sub, error } = await supabase
          .from('company_subscriptions')
          .select('max_file_size_mb, max_files_per_upload, limit_total_upload_mb, limit_total_files, status')
          .eq('company_id', userCompany.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error) {
          console.warn('Failed to fetch company_subscriptions for constraints:', error);
          return;
        }
        const limitTotalUploadMb = typeof sub?.limit_total_upload_mb === 'number' ? sub.limit_total_upload_mb : null;
        const limitTotalFiles = typeof sub?.limit_total_files === 'number' ? sub.limit_total_files : null;
        setUploadConstraints({
          maxFileSizeMb: typeof sub?.max_file_size_mb === 'number' ? sub.max_file_size_mb : null,
          maxFilesPerUpload: typeof sub?.max_files_per_upload === 'number' ? sub.max_files_per_upload : null,
          limitTotalUploadMb,
          limitTotalFiles,
        });

        // Optionally fetch current used total upload from quotas for display (align with server logic which uses current period row)
        try {
          const { data: quotaRow, error: quotaErr } = await supabase
            .from('company_quotas')
            .select('used_total_upload, usage_period_start')
            .eq('company_id', userCompany.id)
            .order('usage_period_start', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (quotaErr) {
            console.warn('Failed to fetch company_quotas for totals:', quotaErr);
            setUploadTotals({ usedTotalUploadMb: null, remainingTotalUploadMb: null, currentFileCount: null });
          } else {
            const usedMb = typeof quotaRow?.used_total_upload === 'number' ? quotaRow.used_total_upload : 0;
            const remainingMb = typeof limitTotalUploadMb === 'number' ? Math.max(limitTotalUploadMb - usedMb, 0) : null;
            setUploadTotals({ usedTotalUploadMb: usedMb, remainingTotalUploadMb: remainingMb, currentFileCount: null });
          }
        } catch (innerErr) {
          console.warn('Error loading used total upload:', innerErr);
          setUploadTotals({ usedTotalUploadMb: null, remainingTotalUploadMb: null, currentFileCount: null });
        }
        
        // Fetch current file count for the company
        try {
          const { count, error: countErr } = await supabase
            .from('user_files')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', userCompany.id);
          if (countErr) {
            console.warn('Failed to fetch company file count:', countErr);
          } else {
            setUploadTotals(prev => ({ ...prev, currentFileCount: count || 0 }));
          }
        } catch (countErr) {
          console.warn('Error loading company file count:', countErr);
        }
      } catch (e) {
        console.warn('Error loading upload constraints:', e);
      }
    };
    fetchUploadConstraints();
  }, [userCompany]);

  const validateFilesAgainstConstraints = (incomingFiles) => {
    const maxFiles = uploadConstraints.maxFilesPerUpload;
    const maxSizeMb = uploadConstraints.maxFileSizeMb;
    const maxSizeBytes = typeof maxSizeMb === 'number' && maxSizeMb > 0 ? maxSizeMb * 1024 * 1024 : null;

    // Enforce max files per upload
    if (typeof maxFiles === 'number' && maxFiles >= 0 && incomingFiles.length > maxFiles) {
      return { validFiles: [], error: `You can upload at most ${maxFiles} file(s) per upload.` };
    }

    // Enforce max file size
    if (maxSizeBytes) {
      const tooLarge = incomingFiles.filter(f => f.size > maxSizeBytes);
      if (tooLarge.length > 0) {
        const names = tooLarge.map(f => f.name).join(', ');
        return { validFiles: [], error: `These file(s) exceed the ${maxSizeMb} MB limit: ${names}` };
      }
    }

    return { validFiles: incomingFiles, error: '' };
  };

  // Delay helper function
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Format MB values for display
  const formatMb = (value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '0.00';
    return value.toFixed(2);
  };

  // Wait for Supabase to be up by repeatedly calling an API method
  const waitForSupabase = async (maxAttempts = 10, delayMs = 2000) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Try a simple Supabase call (getSession here)
        const { data, error } = await supabase.auth.getSession();
        // Even if there's no active session, if there's no error, Supabase is up
        if (!error) {
          console.log("Supabase is up on attempt", attempt);
          return;
        }
      } catch (err) {
        console.log(`Supabase connection attempt ${attempt} failed: ${err.message}`);
      }
      await delay(delayMs);
    }
    throw new Error("Supabase not available after waiting");
  };

  // Helper to refresh upload totals and limits (used after upload/delete)
  const refreshUploadTotals = async (companyId) => {
    try {
      const { data: sub } = await supabase
        .from('company_subscriptions')
        .select('limit_total_upload_mb, limit_total_files')
        .eq('company_id', companyId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      const limitTotalUploadMb = typeof sub?.limit_total_upload_mb === 'number' ? sub.limit_total_upload_mb : null;
      const limitTotalFiles = typeof sub?.limit_total_files === 'number' ? sub.limit_total_files : null;

      const { data: quotaRow } = await supabase
        .from('company_quotas')
        .select('used_total_upload, usage_period_start')
        .eq('company_id', companyId)
        .order('usage_period_start', { ascending: false })
        .limit(1)
        .maybeSingle();

      const usedMb = typeof quotaRow?.used_total_upload === 'number' ? quotaRow.used_total_upload : 0;
      const remainingMb = typeof limitTotalUploadMb === 'number' ? Math.max(limitTotalUploadMb - usedMb, 0) : null;
      
      // Fetch current file count
      const { count } = await supabase
        .from('user_files')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', companyId);
      
      setUploadConstraints(prev => ({ ...prev, limitTotalUploadMb, limitTotalFiles }));
      setUploadTotals({ usedTotalUploadMb: usedMb, remainingTotalUploadMb: remainingMb, currentFileCount: count || 0 });
    } catch (e) {
      console.warn('refreshUploadTotals failed', e);
    }
  };

  // --- NEW: Upload progress tracking functions ---
  const fetchUploadProgress = async (fileIds = null, batchId = null) => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      let url = `${BACKEND_URL}/upload_progress`;
      const params = [];
      
      if (fileIds) {
        params.push(`file_ids=${fileIds.join(',')}`);
      }
      if (batchId) {
        params.push(`crawl_batch_id=${batchId}`);
      }
      
      if (params.length > 0) {
        url += '?' + params.join('&');
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUploadProgress(response.data);

      // If all files are complete or failed, stop polling
      if (response.data.processing === 0) {
        stopProgressPolling();
      }

      return response.data;
    } catch (err) {
      console.error('Failed to fetch upload progress:', err);
      return null;
    }
  };

  const startProgressPolling = (fileIds = null, batchId = null) => {
    // Clear any existing interval
    if (progressPollInterval) {
      clearInterval(progressPollInterval);
    }

    // Show snackbar
    setShowProgressSnackbar(true);

    // Initial fetch
    fetchUploadProgress(fileIds, batchId);

    // Poll every 3 seconds
    const interval = setInterval(() => {
      fetchUploadProgress(fileIds, batchId);
    }, 3000);

    setProgressPollInterval(interval);
  };

  const stopProgressPolling = () => {
    if (progressPollInterval) {
      clearInterval(progressPollInterval);
      setProgressPollInterval(null);
    }
    // Keep snackbar visible for 5 more seconds after completion
    setTimeout(() => {
      setShowProgressSnackbar(false);
      setUploadProgress(null);
    }, 5000);
  };

  // Cleanup progress polling on unmount
  useEffect(() => {
    return () => {
      if (progressPollInterval) {
        clearInterval(progressPollInterval);
      }
    };
  }, [progressPollInterval]);
  // --- END NEW: Upload progress tracking functions ---

  // --- Sorting Helper Functions ---
  function descendingComparator(a, b, orderBy) {
    // Handle potential null/undefined values safely
    const valA = a[orderBy] || ''; 
    const valB = b[orderBy] || '';
    if (valB < valA) {
      return -1;
    }
    if (valB > valA) {
      return 1;
    }
    return 0;
  }

  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  // This method is needed for supporting consistent sorting across browsers
  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }
  // --- End Sorting Helper Functions ---

  // --- Sort Request Handler ---
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  // --- End Sort Request Handler ---

  // getAuthToken now retries if Supabase isn't ready or there's no session
  const getAuthToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  }, []);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    setMessage('');
    setError('');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    document.body.style.cursor = "wait";

    if (files.length === 0) {
      setError('Please select a file to upload.');
      document.body.style.cursor = "default";
      setIsUploading(false);
      return;
    }

    try {
      const token = await getAuthToken();
      // Loop over each file for upload
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const maxAttempts = 3;
        let attempt = 0;
        let uploaded = false;
        
        while (attempt < maxAttempts && !uploaded) {
          try {
            const response = await axios.post(
              `${BACKEND_URL}/upload_document`,
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                  Authorization: `Bearer ${token}`,
                },
                timeout: 60000, // 60-second timeout
              }
            );
            console.log('File uploaded successfully, Please wait 1-5 minutes to process to chat with the document:', response.data);
            setMessage(prev => prev + `File ${file.name} uploaded successfully. Please wait 1-5 minutes to process to chat with the document\n`);
            uploaded = true;
          } catch (err) {
            attempt++;
            if (err.response && err.response.status === 409) {
              const duplicateMessage = `File '${file.name}' already exists and was skipped.`;
              console.warn(`Duplicate file detected: ${file.name}`);
              setMessage(prev => (prev ? prev + "\n" : "") + duplicateMessage);
              uploaded = true;
              break;
            } else {
              const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Unknown upload error occurred';
              console.error(`Attempt ${attempt} - Error uploading file '${file.name}':`, errorMessage);
              if (attempt === maxAttempts) {
                setError(prev => (prev ? prev + "\n" : "") + `File ${file.name} failed after ${maxAttempts} attempts: ${errorMessage}`);
              }
              if (attempt < maxAttempts) {
                  await delay(2000 * attempt);
              }
            }
          }
        }
      }
      // After processing all files, refresh the list
      await fetchUploadedFiles();
      setFiles([]); // Clear the selected files
    } catch (err) {
      console.error('Error during upload process:', err);
      setError("An error occurred during upload.");
    } finally {
      document.body.style.cursor = "default";
      setIsUploading(false);
    }
  };

  // Fetch uploaded files from the backend with retry logic
  async function fetchUploadedFiles() {
    const maxAttempts = 3;
    let attempts = 0;
    while (attempts < maxAttempts) {
      try {
        const { data, error: authError } = await supabase.auth.getSession();
        if (authError) {
          throw new Error(`Supabase auth error: ${authError.message}`);
        }
        if (!data || !data.session || !data.session.access_token) {
          // Allow fetching to proceed if session is missing initially, 
          // let the backend handle token verification if token exists but session is invalid
          // If no token at all, backend should ideally reject anyway, but we can log here.
          console.warn("No active session or token found in frontend. Proceeding with fetch attempt...");
          // Depending on backend implementation, might want to throw here if token is truly needed *before* the call
        }
        const token = data?.session?.access_token || ''; // Use token if available, else empty string
        
        console.log("Sending request to", `${BACKEND_URL}/get_uploaded_files`);
        const response = await axios.get(`${BACKEND_URL}/get_uploaded_files`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 120000,
        });
         
        console.log("Raw data received from /get_uploaded_files:", response.data);
        const receivedFiles = response.data.files || []; // Ensure it's an array
        console.log("Files data before setting state:", receivedFiles);
        setUploadedFiles(receivedFiles);
        const hasProcessingFiles = receivedFiles.some(file => 
          file.processing_status === 'processing' || 
          file.processing_status === 'pending' || 
          file.processing_status === 'chunking' ||
          file.processing_status === 'embedding' || 
          file.processing_status === 'unknown'
        );
        console.log(`Polling check: hasProcessingFiles = ${hasProcessingFiles}`);
        
        // NEW: Don't auto-start progress tracking here - it will be started by upload/crawl actions
        // This prevents the progress box from appearing on every refresh when there are processing files
        // Progress tracking should only be shown for newly initiated uploads/crawls
        
        if (hasProcessingFiles) {
          console.log(`Scheduling next poll in 3 seconds because hasProcessingFiles is true.`);
          setTimeout(() => fetchUploadedFiles(), 3000);
        } else {
          console.log("All files processed (or no processing files found), stopping polling.");
          setPage(0);
        }

        return; // Exit the loop successfully

      } catch (err) {
        attempts++;
        console.error(`Attempt ${attempts} - Error fetching files:`, err);

        // Check specifically for 401/403 Authentication/Authorization errors
        if (err.response?.status === 401 || err.response?.status === 403) {
          console.warn("Authentication error (401/403) detected fetching files. Logging out user.");
          setError("Your session may have expired or is invalid. Please log in again.");
          if (handleLogout) {
            await handleLogout();
          } else {
            console.error("handleLogout prop not passed to FileUpload component!");
          }
          return;
        }

        // Log detailed error if available (for other types of errors)
        if (err.response) {
            console.error('Error response data:', err.response.data);
            console.error('Error response status:', err.response.status);
            console.error('Error response headers:', err.response.headers);
        } else if (err.request) {
            console.error('Error request:', err.request);
        } else {
            console.error('Error message:', err.message);
        }

        // If it wasn't 401/403 and retries are exhausted, set generic error
        if (attempts >= maxAttempts) {
          setError("Failed to load files after multiple attempts.");
          return; // Stop polling on persistent error
        }
        await delay(2000 * attempts); // Wait before retrying (only for non-auth errors)
      }
    }
  }

  // Add useEffect to log state AFTER update confirms
  useEffect(() => {
    console.log("uploadedFiles state AFTER confirmed update:", uploadedFiles);
  }, [uploadedFiles]);

  // Cleanup polling timer on unmount
  // const timerIdRef = useRef(null); // Add this near other useRef declarations
  // useEffect(() => {
  //   return () => {
  //     if (timerIdRef.current) {
  //       clearTimeout(timerIdRef.current);
  //     }
  //   };
  // }, []);

  // In useEffect, wait for Supabase to be up before fetching files
  useEffect(() => {
    console.log("FileUpload component mounted/updated");
    const init = async () => {
      try {
        console.log("Waiting for Supabase to be ready...");
        await waitForSupabase();
        console.log("Supabase is ready, fetching files...");
        await fetchUploadedFiles();
      } catch (err) {
        console.error("Error waiting for Supabase:", err);
        setError("Error connecting to Supabase. Please try again later.");
      }
    };
    init();
  }, []);

  const handleChatNavigation = () => {
    navigate('/chat');
  };

  const handleWebpageUrlChange = (e) => {
    setWebpageUrl(e.target.value);
  };

  // Add new handler for sitemap URL
  const handleSitemapUrlChange = (e) => {
    let url = e.target.value;
    // If URL doesn't start with http:// or https://, add https://
    if (url && url.trim() !== '' && !url.match(/^https?:\/\//)) {
      url = 'https://' + url;
    }
    setSitemapUrl(url);
  };

  // Utility function for retrying API calls with exponential backoff
  const retryApiCall = async (apiCallFn, maxRetries = 3, initialDelay = 1000) => {
    let lastError;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await apiCallFn();
      } catch (error) {
        lastError = error;
        
        // Check if it's a network error that should be retried
        const isNetworkError = 
          error.message?.includes('ERR_NETWORK_CHANGED') ||
          error.message?.includes('Network Error') ||
          error.message?.includes('ERR_CONNECTION') ||
          error.code === 'ECONNABORTED' ||
          error.code === 'ECONNRESET' ||
          !error.response; // No response usually means network issue
        
        if (!isNetworkError || attempt === maxRetries) {
          throw error; // Not a network error or final attempt - throw immediately
        }
        
        // Calculate delay with exponential backoff
        const delay = initialDelay * Math.pow(2, attempt - 1);
        console.log(`Network error on attempt ${attempt}/${maxRetries}, retrying in ${delay}ms...`);
        setMessage(`Network error, retrying... (attempt ${attempt}/${maxRetries})`);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    throw lastError;
  };

  // Unified sitemap function that tries direct XML first, then falls back to extraction
  const smartSitemapExtraction = async () => {
    try {
      // Trim and validate URL
      const trimmedUrl = sitemapUrl.trim();
      if (!trimmedUrl) {
        setError('Please enter a valid URL to extract sitemap.');
        return;
      }
      
      // Ensure URL has protocol
      let baseUrl = trimmedUrl;
      if (!baseUrl.match(/^https?:\/\//)) {
        baseUrl = 'https://' + baseUrl;
      }
      
      // Start loading state
      setIsLoadingSitemap(true);
      setMessage('');
      setError('');
      document.body.style.cursor = "wait";
      
      const token = await getAuthToken();
      console.log('Starting smart sitemap extraction for:', baseUrl);
      
      // First try: Direct sitemap.xml URL
      let directUrl = baseUrl;
      
      // If URL doesn't already end with sitemap.xml, sitemap_index.xml, sitemap-*.xml, or any .xml in /sitemap/ path, append sitemap.xml
      const looksLikeSitemap = (
        /sitemap(_index)?\.xml$/i.test(directUrl) ||  // sitemap.xml or sitemap_index.xml
        /sitemap-\d+\.xml$/i.test(directUrl) ||         // sitemap-1.xml, sitemap-19.xml, etc.
        (directUrl.endsWith('.xml') && /\/sitemap/i.test(directUrl))  // any .xml in /sitemap/ path
      );
      
      if (!looksLikeSitemap) {
        // Remove trailing slash if present
        directUrl = directUrl.replace(/\/$/, '');
        
        // Try www subdomain version if not already present
        if (!directUrl.includes('://www.')) {
          directUrl = directUrl.replace(/(https?:\/\/)([^\/]+)/, '$1www.$2');
        }
        
        // Append sitemap.xml
        directUrl += '/sitemap.xml';
      }
      
      console.log('Trying direct sitemap URL:', directUrl);
      
      // Try the direct XML URL first WITH RETRY
      try {
        const response = await retryApiCall(async () => {
          return await axios.post(
            `${BACKEND_URL}/extract_sitemap_urls`,
            { url: directUrl, ignore_robots_txt: ignoreRobotsTxt },
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              timeout: 180000 // 3-minute timeout
            }
          );
        });
        
        console.log('Direct sitemap URL worked:', directUrl);
        console.log('Sitemap URLs extracted:', response.data);
        
        // Store the URL hierarchy and flat URL list
        setSitemapUrlHierarchy(response.data.url_hierarchy || {});
        setFlatUrls(response.data.flat_urls || []);

        // Build summary message
        let summaryMsg = `Found ${response.data.total_urls} URL${response.data.total_urls !== 1 ? 's' : ''}`;
        
        // Show warning if URLs were truncated
        if (response.data.truncated && response.data.total_urls > response.data.displayed_urls) {
          summaryMsg += ` (showing first ${response.data.displayed_urls.toLocaleString()} to prevent browser issues)`;
          if (response.data.warning) {
            setError(response.data.warning);
          }
        }
        
        // Add robots.txt filtering info if present
        if (response.data?.robots_txt_filtering) {
          const robotsStats = response.data.robots_txt_filtering;
          if (robotsStats.disallowed_count > 0) {
            summaryMsg += ` (${robotsStats.disallowed_count} URL${robotsStats.disallowed_count !== 1 ? 's' : ''} disallowed by robots.txt)`;
          } else {
            summaryMsg += ` (all allowed by robots.txt)`;
          }
        } else if (ignoreRobotsTxt) {
          summaryMsg += ` (robots.txt check skipped)`;
        }
        
        // Add debug counts if available
        if (response.data?.debug) {
          const dbg = response.data.debug;
          if (typeof dbg.sitemap_count === 'number' || typeof dbg.js_count === 'number') {
            const sm = typeof dbg.sitemap_count === 'number' ? dbg.sitemap_count : 0;
            const js = typeof dbg.js_count === 'number' ? dbg.js_count : 0;
            summaryMsg += ` - Source: sitemap ${sm}, JS ${js}`;
          } else if (dbg.discovery_method === 'js_fallback' && typeof dbg.js_count === 'number') {
            summaryMsg += ` via JS fallback`;
          }
        }
        
        setMessage(summaryMsg);
        
        // Clear any selected URLs from previous operations
        setSelectedUrls([]);
        
        // Force render update before showing modal
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Show the URL selection modal
        console.log('Opening URL selection modal with', response.data.flat_urls.length, 'URLs');
        setShowUrlSelectionModal(true);
        
        return; // Success with direct approach
      } catch (err) {
        console.log('Direct sitemap approach failed, falling back to extraction:', err.message);
        // Fall through to the extraction approach
      }
      
      // Second try: Use the original extraction method with the base URL WITH RETRY
      console.log('Falling back to extraction method with:', baseUrl);
      const response = await retryApiCall(async () => {
        return await axios.post(
          `${BACKEND_URL}/extract_sitemap_urls`,
          { url: baseUrl, ignore_robots_txt: ignoreRobotsTxt },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            timeout: 180000 // 3-minute timeout
          }
        );
      });
      
      console.log('Sitemap URLs extracted through fallback method:', response.data);
      
      // Store the URL hierarchy and flat URL list
      setSitemapUrlHierarchy(response.data.url_hierarchy || {});
      setFlatUrls(response.data.flat_urls || []);

      // Build summary message
      let summaryMsg = `Found ${response.data.total_urls} URL${response.data.total_urls !== 1 ? 's' : ''}`;
      
      // Show warning if URLs were truncated
      if (response.data.truncated && response.data.total_urls > response.data.displayed_urls) {
        summaryMsg += ` (showing first ${response.data.displayed_urls.toLocaleString()} to prevent browser issues)`;
        if (response.data.warning) {
          setError(response.data.warning);
        }
      }
      
      // Add robots.txt filtering info if present
      if (response.data?.robots_txt_filtering) {
        const robotsStats = response.data.robots_txt_filtering;
        if (robotsStats.disallowed_count > 0) {
          summaryMsg += ` (${robotsStats.disallowed_count} URL${robotsStats.disallowed_count !== 1 ? 's' : ''} disallowed by robots.txt)`;
        } else {
          summaryMsg += ` (all allowed by robots.txt)`;
        }
      } else if (ignoreRobotsTxt) {
        summaryMsg += ` (robots.txt check skipped)`;
      }
      
      // Add debug counts if available
      if (response.data?.debug) {
        const dbg = response.data.debug;
        if (typeof dbg.sitemap_count === 'number' || typeof dbg.js_count === 'number') {
          const sm = typeof dbg.sitemap_count === 'number' ? dbg.sitemap_count : 0;
          const js = typeof dbg.js_count === 'number' ? dbg.js_count : 0;
          summaryMsg += ` - Source: sitemap ${sm}, JS ${js}`;
        } else if (dbg.discovery_method === 'js_fallback' && typeof dbg.js_count === 'number') {
          summaryMsg += ` via JS fallback`;
        }
      }
      
      setMessage(summaryMsg);
      
      // Clear any selected URLs from previous operations
      setSelectedUrls([]);
      
      // Force render update before showing modal
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Show the URL selection modal
      console.log('Opening URL selection modal with', response.data.flat_urls.length, 'URLs');
      setShowUrlSelectionModal(true);
      
    } catch (err) {
      console.error('Error in smart sitemap extraction:', err);
      
      // Extract error detail from response
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error occurred';
      
      // Check for suggested alternative URLs in the error message
      if (errorMessage.includes("You might try")) {
        const suggestedUrlMatch = errorMessage.match(/You might try (https?:\/\/[^\s]+) instead/);
        if (suggestedUrlMatch && suggestedUrlMatch[1]) {
          const suggestedUrl = suggestedUrlMatch[1];
          
          // Create a custom error message with a clickable link
          setError(
            <div>
              {errorMessage.split("You might try")[0]}
              <br />
              Try instead:{' '}
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  setSitemapUrl(suggestedUrl);
                  smartSitemapExtraction();
                }}
                style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
              >
                {suggestedUrl}
              </a>
            </div>
          );
        } else {
          setError(`Error extracting sitemap URLs: ${errorMessage}`);
        }
      } else {
        setError(`Error extracting sitemap URLs: ${errorMessage}`);
      }
    } finally {
      setIsLoadingSitemap(false);
      document.body.style.cursor = "default";
    }
  };

  // Function to handle URL selection
  const handleUrlSelection = (url, isChecked) => {
    setSelectedUrls(prevSelected => {
      if (isChecked) {
        // Add URL if not already in the array
        return [...new Set([...prevSelected, url])];
      } else {
        // Remove URL if it's in the array
        return prevSelected.filter(u => u !== url);
      }
    });
  };

  // Function to select all URLs in a section
  const handleSectionSelection = (urls, isChecked) => {
    setSelectedUrls(prevSelected => {
      if (isChecked) {
        // Add all URLs in this section
        return [...new Set([...prevSelected, ...urls])];
      } else {
        // Remove all URLs in this section
        return prevSelected.filter(u => !urls.includes(u));
      }
    });
  };

  // Function to handle select all
  const handleSelectAllUrls = (isChecked) => {
    if (isChecked) {
      setSelectedUrls([...flatUrls]);
    } else {
      setSelectedUrls([]);
    }
  };

  // Check if all URLs in a section are selected
  const isSectionFullySelected = (urls) => {
    return urls.every(url => selectedUrls.includes(url));
  };

  // Check if some URLs in a section are selected
  const isSectionPartiallySelected = (urls) => {
    return !isSectionFullySelected(urls) && urls.some(url => selectedUrls.includes(url));
  };

  // Recursive component to render URL hierarchy
  const UrlHierarchyItem = ({ name, data, level = 0 }) => {
    // Extract all URLs at this level and below
    const getAllUrls = (data) => {
      let urls = [];
      
      // Add URLs at current level
      if (data.__urls) {
        urls = [...urls, ...data.__urls.map(item => item.url)];
      }
      
      // Add URLs from child nodes
      Object.keys(data).forEach(key => {
        if (key !== '__urls' && typeof data[key] === 'object') {
          urls = [...urls, ...getAllUrls(data[key])];
        }
      });
      
      return urls;
    };
    
    const urls = data.__urls ? data.__urls.map(item => item.url) : [];
    const allUrls = getAllUrls(data);
    const isFullySelected = isSectionFullySelected(allUrls);
    const isPartiallySelected = isSectionPartiallySelected(allUrls);
    
    // Don't render empty sections
    if (allUrls.length === 0) {
      return null;
    }
    
    return (
      <Box sx={{ 
        ml: level * 2, 
        mb: level === 0 ? 3 : 1,
        border: level === 0 ? '1px solid #eaeaea' : 'none',
        borderRadius: level === 0 ? 1 : 0,
        pb: level === 0 ? 2 : 0,
        pt: level === 0 ? 1 : 0,
        pl: level === 0 ? 2 : 0,
        pr: level === 0 ? 2 : 0,
        backgroundColor: level === 0 ? '#fafafa' : 'transparent'
      }}>
        {/* Section header with checkbox */}
        {name && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            my: 1,
            borderBottom: level === 0 ? '1px solid #eaeaea' : 'none',
            pb: level === 0 ? 1 : 0,
          }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={isFullySelected}
                  indeterminate={isPartiallySelected && !isFullySelected}
                  onChange={(e) => handleSectionSelection(allUrls, e.target.checked)}
                  color={level === 0 ? "primary" : "default"}
                />
              }
              label={
                <Typography 
                  variant={level === 0 ? "subtitle1" : "body1"} 
                  sx={{ 
                    fontWeight: level === 0 ? 'bold' : 'medium',
                    color: level === 0 ? 'primary.main' : 'text.primary',
                    fontSize: level === 0 ? '1rem' : '0.9rem'
                  }}
                >
                  {name}
                </Typography>
              }
            />
            <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
              ({allUrls.length} URL{allUrls.length !== 1 ? 's' : ''})
            </Typography>
          </Box>
        )}
        
        {/* URLs at this level - only show if there are 5 or fewer URLs or if this is the deepest level */}
        {urls.length > 0 && (
          <List dense disablePadding sx={{ ml: 2 }}>
            {urls.map((url, idx) => {
              const urlData = data.__urls.find(item => item.url === url);
              return (
                <ListItem 
                  key={idx} 
                  dense 
                  disablePadding 
                  sx={{ py: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={selectedUrls.includes(url)}
                      onChange={(e) => handleUrlSelection(url, e.target.checked)}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ 
                            fontSize: '0.875rem',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            maxWidth: '70%',
                            display: 'inline-block'
                          }}
                        >
                          {urlData.title}
                        </Typography>
                        {/* Clickable URL link - opens in new tab */}
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            fontSize: '0.75rem',
                            color: '#1976d2',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '2px'
                          }}
                          title={`Open ${url} in new tab`}
                        >
                          🔗
                        </a>
                        {/* Show robots noindex if detected */}
                        {urlData.robots_noindex && (
                          <Typography
                            variant="caption"
                            sx={{
                              bgcolor: '#ff9800',
                              color: 'white',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 0.5,
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                              textTransform: 'uppercase'
                            }}
                            title="This page has robots: noindex meta tag"
                          >
                            ROBOTS: NOINDEX
                          </Typography>
                        )}
                        {/* Show pattern if detected */}
                        {urlData.pattern && (
                          <Typography
                            variant="caption"
                            sx={{
                              bgcolor: '#9c27b0',
                              color: 'white',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 0.5,
                              fontSize: '0.65rem'
                            }}
                            title={`URL matches pattern: ${urlData.pattern}`}
                          >
                            {urlData.pattern}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
        
        {/* Render child sections */}
        {Object.keys(data).filter(key => key !== '__urls').map((key, idx) => (
          <UrlHierarchyItem 
            key={idx}
            name={key} 
            data={data[key]} 
            level={level + 1} 
          />
        ))}
      </Box>
    );
  };

  // Simple function to crawl webpages
  const crawlWebpage = async () => {
    if (!webpageUrl) {
      setError('Please enter a URL to crawl.');
      return;
    }

    setIsCrawling(true);
    setMessage('');
    setError('');
    document.body.style.cursor = "wait";

    try {
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${BACKEND_URL}/crawl_webpage`,
        { url: webpageUrl },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          timeout: 120000 // 2-minute timeout for crawling
        }
      );

      console.log('Webpage crawled successfully:', response.data);
      setMessage(`Webpage crawled and saved as ${response.data.file_name}`);
      
      // Refresh the file list after crawling
      await fetchUploadedFiles();
      setWebpageUrl(''); // Clear the URL input
    } catch (err) {
      console.error('Error crawling webpage:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error occurred';
      setError(`Error crawling webpage: ${errorMessage}`);
    } finally {
      setIsCrawling(false);
      document.body.style.cursor = "default";
    }
  };

  // Update the existing crawlSitemap function
  const crawlSitemap = async () => {
    try {
      console.log("Starting sitemap crawl process for URL:", sitemapUrl);
      
      // If URL is empty, show error
      if (!sitemapUrl.trim()) {
        setError('Please enter a website URL or sitemap URL to extract sitemap.');
        return;
      }
      
      // Call smartSitemapExtraction to get the URLs and show selection modal
      await smartSitemapExtraction();
      
      // Log when extraction is complete
      console.log("Sitemap URL extraction complete");
    } catch (err) {
      console.error("Error in crawlSitemap:", err);
      setError(`Error processing sitemap: ${err.message}`);
    }
  };

  // Add new function to handle file selection
  const handleFileSelection = (filename, isChecked) => {
    setSelectedFiles(prevSelected => {
      if (isChecked) {
        return [...prevSelected, filename];
      } else {
        return prevSelected.filter(f => f !== filename);
      }
    });
  };

  // --- NEW: Filter and search logic ---
  const filteredAndSearchedFiles = React.useMemo(() => {
    return uploadedFiles.filter(file => {
      // 1. Apply type/team filter
      const isCrawled = file.source_url && !file.source_url.startsWith(file.user_id || 'user_');
      
      if (filterType === 'crawled' && !isCrawled) return false;
      if (filterType === 'uploaded' && isCrawled) return false;
      if (filterType === 'personal' && (file.company_id || file.team_id)) return false;
      if (filterType === 'company' && (!file.company_id || file.team_id)) return false;
      
      // If filterType is a team_id (UUID format)
      if (filterType !== 'all' && filterType !== 'crawled' && filterType !== 'uploaded' && 
          filterType !== 'personal' && filterType !== 'company') {
        // It's a team ID
        if (file.team_id !== filterType) return false;
      }
      
      // 2. Apply search query
      if (searchQuery && searchQuery.trim() !== '') {
        const searchLower = searchQuery.toLowerCase();
        const matchesUrl = file.source_url?.toLowerCase().includes(searchLower);
        const matchesFilename = file.filename?.toLowerCase().includes(searchLower);
        return matchesUrl || matchesFilename;
      }
      
      return true;
    });
  }, [uploadedFiles, filterType, searchQuery]);
  // --- END NEW ---

  // --- Memoized calculation for visible files --- 
  const visibleFiles = React.useMemo(() => {
    return stableSort(filteredAndSearchedFiles, getComparator(order, orderBy))
             .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredAndSearchedFiles, order, orderBy, page, rowsPerPage]);
  // ----------------------------------------------

  // --- Calculate header checkbox state --- 
  const numSelectedOnPage = React.useMemo(() => 
     visibleFiles.filter(f => selectedFiles.includes(f.filename)).length
  , [visibleFiles, selectedFiles]);
  
  const allOnPageSelected = visibleFiles.length > 0 && numSelectedOnPage === visibleFiles.length;
  const someOnPageSelected = visibleFiles.length > 0 && numSelectedOnPage > 0 && !allOnPageSelected;
  // ----------------------------------------

  // --- Handlers (handleSelectAllFiles needs updated logic) --- 
  const handleSelectAllFiles = useCallback((event) => {
    const isChecked = event.target.checked;
    // Get the filenames of the currently visible files
    const visibleFilenames = visibleFiles.map(f => f.filename);

    if (isChecked) {
      // Add only visible filenames to the selection
      setSelectedFiles(prevSelected => [...new Set([...prevSelected, ...visibleFilenames])]);
    } else {
      // Remove only visible filenames from the selection
      setSelectedFiles(prevSelected => prevSelected.filter(f => !visibleFilenames.includes(f)));
    }
  }, [visibleFiles]); // Depend on visibleFiles now
  // ----------------------------------------------------------

  // Modify handleDelete to handle batch deletion
  const handleDelete = async () => {
    // --- Add logging --- 
    console.log("--- handleDelete function entered ---");
    // -------------------

    if (selectedFiles.length === 0) {
      // --- Add logging --- 
      console.log("handleDelete: No files selected, exiting.");
      // -------------------
      setError('Please select at least one file to delete.');
      return;
    }
    
    // --- Add logging --- 
    console.log("handleDelete: Asking for confirmation...");
    // -------------------
    if (!window.confirm(`Are you sure you want to delete ${selectedFiles.length} selected file(s)?`)) {
      // --- Add logging --- 
      console.log("handleDelete: User cancelled confirmation, exiting.");
      // -------------------
      return;
    }
    // --- Add logging --- 
    console.log("handleDelete: User confirmed.");
    // -------------------
    
    // --- Add logging --- 
    console.log(`Starting batch deletion for ${selectedFiles.length} files:`, selectedFiles);
    // -------------------
    
    // Clear any existing timer to prevent interference
    if (deleteTimerId) {
      clearInterval(deleteTimerId);
    }
    
    // Set deleting state and start timer
    setIsDeleting(true);
    setDeleteTimer(0);
    document.body.style.cursor = "wait";
    
    // Start a timer that increments every second
    const timerId = setInterval(() => {
      setDeleteTimer(prevTime => prevTime + 1);
    }, 1000);
    
    // Store the new timer ID
    setDeleteTimerId(timerId);
    
    try {
      const token = await getAuthToken();
      let successCount = 0;
      let errorCount = 0;
      const notOwnerMessages = [];

      // Delete each selected file one by one
      for (const filename of selectedFiles) {
        try {
          setDeletingFile(filename); // Update the currently deleting file
          // --- Add logging --- 
          console.log(`Attempting to delete: ${filename}`);
          // -------------------
          const deleteUrl = `${BACKEND_URL}/delete_file/${encodeURIComponent(filename)}`;
          const response = await axios.delete(deleteUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            timeout: 60000,
          });
          console.log('File deleted successfully:', response.data); // Keep existing log
          // --- Add logging --- 
          console.log(`Successfully requested deletion for: ${filename}`);
          // -------------------
          successCount++;
        } catch (err) {
          // --- Add logging --- 
          console.error(`Error deleting file ${filename}:`, err.response?.data || err.message);
          // -------------------
          errorCount++;
          try {
            if (err.response?.status === 403 && (err.response?.data?.code === 'not_owner' || err.response?.data?.status === 'forbidden')) {
              const ownerEmail = err.response?.data?.owner_email;
              notOwnerMessages.push(`Cannot delete '${filename}': it was uploaded by ${ownerEmail || 'another user in your company'}. Please ask the owner or a company admin to delete it.`);
            }
          } catch {}
        }
      }
      
      // --- Add logging --- 
      console.log(`Deletion loop finished. Success: ${successCount}, Errors: ${errorCount}`);
      console.log('Attempting to fetch updated file list...');
      // -------------------

      // Fetch updated files list
      await fetchUploadedFiles();
      
      // --- Add logging --- 
      console.log('Finished fetching updated file list.');
      // -------------------
      
      // Show success message
      let message = `Successfully deleted ${successCount} file(s).`;
      if (errorCount > 0) {
        message += ` ${errorCount} file(s) failed to delete.`;
      }
      setMessage(message);
      if (notOwnerMessages.length > 0) {
        setError(notOwnerMessages.join('\n'));
      } else {
        setError('');
      }
      
      // Clear selections
      setSelectedFiles([]);
      
      // Set a timer to automatically clear the message after 3 seconds
      setTimeout(() => {
        setMessage('');
      }, 3000);
      
      // Clear any previous error messages handled above
    } catch (err) {
      console.error('Error during batch deletion:', err.message);
      setError(`Error during batch deletion: ${err.message}`);
      // Refresh file list even on error to ensure UI is in sync
      await fetchUploadedFiles();
    } finally {
      // Reset states
      setIsDeleting(false);
      setDeletingFile('');
      
      // Clear the current timer
      clearInterval(timerId);
      setDeleteTimerId(null);
      document.body.style.cursor = "default";
      // After deletions, refresh totals if company context is known
      if (userCompany?.id) await refreshUploadTotals(userCompany.id);
    }
  };

  // --- Handlers for Pagination ---
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page when rows per page changes
  };
  // --- End Pagination Handlers ---

  // Fetch user's company and teams on mount or when currentUser changes
  useEffect(() => {
    const fetchCompanyAndTeams = async () => {
      if (!currentUser) return;
      setLoadingTeams(true);
      try {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('company_id')
          .eq('user_id', currentUser.id)
          .single();

        if (profileError) throw profileError;

        if (profile && profile.company_id) {
          const { data: company, error: companyError } = await supabase
            .from('companies')
            .select('company_id, name')
            .eq('company_id', profile.company_id)
            .single();
          if (companyError) throw companyError;
          setUserCompany({ id: company.company_id, name: company.name });

          // Fetch teams for this company
          const token = await getAuthToken();
          const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;
          const teamsResponse = await fetch(`${BACKEND_URL}/functions/v1/get-company-teams`, {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'apikey': anonKey
            },
          });
          const teamsResult = await teamsResponse.json();
          if (!teamsResponse.ok) throw new Error(teamsResult.error || 'Failed to fetch teams');
          setCompanyTeams(teamsResult.teams || []);
        } else {
          setUserCompany(null);
          setCompanyTeams([]);
        }
      } catch (err) {
        console.error("Error fetching company/teams:", err);
        setError('Could not load your company or team information. Uploads will be personal.');
        setUserCompany(null);
        setCompanyTeams([]);
      } finally {
        setLoadingTeams(false);
      }
    };
    fetchCompanyAndTeams();
  }, [currentUser, getAuthToken]);

  // We need to rename original handleFileChange to avoid conflict
  const handleFileSelectionForUpload = (e) => {
    const selected = Array.from(e.target.files || []);
    setMessage('');
    setError('');
    if (selected.length === 0) {
      setFiles([]);
      return;
    }
    const { validFiles, error: constraintError } = validateFilesAgainstConstraints(selected);
    if (constraintError) {
      setFiles([]);
      setError(constraintError);
      return;
    }
    setFiles(validFiles);

    // Proactive client-side check against remaining total upload MB
    try {
      if (uploadConstraints.limitTotalUploadMb !== null && uploadTotals.remainingTotalUploadMb !== null && uploadScope !== 'personal' && userCompany?.id) {
        const totalSelectedMb = validFiles.reduce((acc, f) => acc + ((f?.size || 0) / (1024 * 1024)), 0);
        if (totalSelectedMb > uploadTotals.remainingTotalUploadMb) {
          const overBy = (totalSelectedMb - uploadTotals.remainingTotalUploadMb).toFixed(2);
          setError(`Selected files exceed remaining company upload limit by ${overBy} MB. Reduce selection or delete existing files.`);
          return; // Do not proceed to upload
        }
      }
    } catch {}

    // After files selected and validated, immediately proceed to upload with the context from modal
    handleUploadSubmit(validFiles);
  };

  const openFileDialog = () => {
    if (fileInputRef) {
        fileInputRef.click();
    }
  };

  // This function is now triggered AFTER scope selection from modal
  const handleUploadSubmit = async (filesToActuallyUpload) => {
    if (!filesToActuallyUpload || filesToActuallyUpload.length === 0) {
        setError('Please select a file to upload.');
        return;
    }
    // Guard: validate again here in case called from elsewhere
    const { validFiles, error: constraintError } = validateFilesAgainstConstraints(filesToActuallyUpload);
    if (constraintError) {
      setError(constraintError);
      return;
    }
    const filesToUpload = validFiles;
    setIsUploading(true);
    document.body.style.cursor = "wait";
    setMessage(''); setError('');

    let finalCompanyId = null;
    let finalTeamId = null;

    if (uploadScope === 'company' && userCompany) {
      finalCompanyId = userCompany.id;
    } else if (uploadScope === 'team' && userCompany && selectedTeamId) {
      finalCompanyId = userCompany.id;
      finalTeamId = selectedTeamId;
    }

    // Client-side preflight for total upload MB limit (company-scoped uploads only)
    if (finalCompanyId && uploadConstraints.limitTotalUploadMb !== null) {
      try {
        const totalSelectedBytes = filesToUpload.reduce((acc, f) => acc + (f?.size || 0), 0);
        const { data: canMore, error: canErr } = await supabase.rpc('can_upload_more_total_mb', { p_company_id: finalCompanyId, p_delta_bytes: totalSelectedBytes });
        if (canErr) {
          console.warn('Preflight total MB check failed:', canErr);
        } else if (canMore === false) {
          const mb = (totalSelectedBytes / (1024 * 1024)).toFixed(2);
          const remaining = (uploadTotals.remainingTotalUploadMb ?? 0).toFixed(2);
          setError(`Selected files total ${mb} MB exceeds remaining upload limit${remaining ? ` (${remaining} MB left)` : ''}.`);
          document.body.style.cursor = "default";
          setIsUploading(false);
          return;
        }
      } catch (pfErr) {
        console.warn('Error during total upload MB preflight:', pfErr);
        // Proceed; server will enforce
      }
    }

    // --- NEW: Start progress tracking ---
    const uploadBatchIds = [];
    // --- END NEW ---

    try {
      const token = await getAuthToken();
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append('file', file);
        if (finalCompanyId) formData.append('company_id', finalCompanyId);
        if (finalTeamId) formData.append('team_id', finalTeamId);

        const maxAttempts = 3;
        let attempt = 0;
        let uploaded = false;
        
        while (attempt < maxAttempts && !uploaded) {
          try {
            const response = await axios.post(
              `${BACKEND_URL}/upload_document`,
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                  Authorization: `Bearer ${token}`,
                },
                timeout: 60000, // 60-second timeout
              }
            );
            console.log('File uploaded successfully, Please wait 1-5 minutes to process to chat with the document:', response.data);
            setMessage(prev => prev + `File ${file.name} (Scope: ${uploadScope}${finalTeamId ? ", Team: "+companyTeams.find(t=>t.team_id===finalTeamId)?.name : ''}) uploaded successfully.\n`);
            
            // --- NEW: Capture upload_batch_id for progress tracking ---
            if (response.data.upload_batch_id) {
              uploadBatchIds.push(response.data.upload_batch_id);
            }
            // --- END NEW ---
            
            uploaded = true;
          } catch (err) {
            attempt++;
            if (err.response && err.response.status === 409) {
              const duplicateMessage = `File '${file.name}' already exists and was skipped.`;
              console.warn(`Duplicate file detected: ${file.name}`);
              setMessage(prev => (prev ? prev + "\n" : "") + duplicateMessage);
              uploaded = true;
              break;
            } else {
              const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Unknown upload error occurred';
              console.error(`Attempt ${attempt} - Error uploading file '${file.name}':`, errorMessage);
              if (attempt === maxAttempts) {
                setError(prev => (prev ? prev + "\n" : "") + `File ${file.name} failed after ${maxAttempts} attempts: ${errorMessage}`);
              }
              if (attempt < maxAttempts) {
                  await delay(2000 * attempt);
              }
            }
          }
        }
      }
      
      // --- NEW: Start progress tracking if files were uploaded ---
      if (uploadBatchIds.length > 0) {
        console.log(`Starting progress tracking for ${uploadBatchIds.length} uploaded files with batch IDs:`, uploadBatchIds);
        // Use the first batch ID for tracking (each file upload gets its own batch ID)
        // For multiple files, we track all by using a comma-separated list or just the first one
        // Since each file is uploaded individually, we'll track by the first batch ID
        // The progress tracker will show progress for files in any of these batches
        const combinedBatchId = uploadBatchIds.join(',');
        startProgressPolling(null, combinedBatchId);
      }
      // --- END NEW ---
      
      await fetchUploadedFiles();
      if (userCompany?.id) await refreshUploadTotals(userCompany.id);
      setFiles([]); 
    } catch (err) {
      console.error('Error during upload process:', err);
      setError("An error occurred during upload.");
    } finally {
      document.body.style.cursor = "default";
      setIsUploading(false);
      setShowTeamContextModal(false); // Close modal after attempting upload
    }
  };

  const handleInitiateUpload = () => {
    setActionToPerform('upload');
    setShowTeamContextModal(true);
  };

  const handleInitiateCrawl = () => {
    if (!webpageUrl) {
      setError('Please enter a URL to crawl.');
      return;
    }
    setActionToPerform('crawl');
    setShowTeamContextModal(true);
  };
  
  const handleInitiateSitemapCrawl = () => {
    if (selectedUrls.length === 0) {
      setError('Please select at least one URL to crawl.');
      return;
    }
    
    // Client-side validation for company/team crawls
    if (uploadScope !== 'personal' && userCompany?.id) {
      // Check file count limit
      if (uploadConstraints.limitTotalFiles !== null && uploadTotals.currentFileCount !== null) {
        const remainingFiles = uploadConstraints.limitTotalFiles - uploadTotals.currentFileCount;
        
        if (remainingFiles <= 0) {
          setError(
            `⚠️ File Limit Reached!\n\n` +
            `Your subscription allows ${uploadConstraints.limitTotalFiles} total files, and you currently have ${uploadTotals.currentFileCount} files.\n\n` +
            `You cannot crawl any more pages until you delete some existing files or upgrade your subscription.`
          );
          return;
        }
        
        if (selectedUrls.length > remainingFiles) {
          const confirmMessage = 
            `⚠️ File Limit Warning!\n\n` +
            `You selected ${selectedUrls.length} URLs, but your subscription only allows ${remainingFiles} more file(s).\n\n` +
            `Current files: ${uploadTotals.currentFileCount}/${uploadConstraints.limitTotalFiles}\n\n` +
            `Only the first ${remainingFiles} pages will be crawled. The remaining ${selectedUrls.length - remainingFiles} URL(s) will be skipped.\n\n` +
            `Do you want to proceed?`;
          
          if (!window.confirm(confirmMessage)) {
            return;
          }
        }
      }
      
      // Check storage size limit
      if (uploadConstraints.limitTotalUploadMb) {
        // Estimate: 150KB per page (conservative estimate based on typical page sizes)
        const estimatedMb = (selectedUrls.length * 150) / 1024;
        
        if (uploadTotals.remainingTotalUploadMb !== null && estimatedMb > uploadTotals.remainingTotalUploadMb) {
          setError(
            `Estimated crawl size (~${estimatedMb.toFixed(1)} MB for ${selectedUrls.length} pages) exceeds your remaining storage limit ` +
            `(${uploadTotals.remainingTotalUploadMb.toFixed(1)} MB). Please reduce selection or delete old files.`
          );
          return;
        }
      }
    }
    
    setActionToPerform('sitemap_crawl');
    setShowTeamContextModal(true);
  };
  
  // Actual crawl logic, called after scope selection
  const performCrawl = async () => {
    setIsCrawling(true);
    setMessage(''); setError('');
    document.body.style.cursor = "wait";

    let finalCompanyId = null;
    let finalTeamId = null;
    if (uploadScope === 'company' && userCompany) finalCompanyId = userCompany.id;
    else if (uploadScope === 'team' && userCompany && selectedTeamId) {
      finalCompanyId = userCompany.id;
      finalTeamId = selectedTeamId;
    }

    try {
      const token = await getAuthToken();
      const payload = { 
        url: webpageUrl, 
        company_id: finalCompanyId,
        team_id: finalTeamId
      };
      
      // Retry logic for network errors
      let response;
      const maxRetries = 3;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          response = await axios.post(`${BACKEND_URL}/crawl_webpage`, payload, {
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            timeout: 120000 
          });
          break; // Success
        } catch (networkErr) {
          const isNetworkError = 
            networkErr.code === 'ERR_NETWORK_CHANGED' ||
            networkErr.code === 'ECONNRESET' ||
            networkErr.code === 'ETIMEDOUT' ||
            networkErr.message?.includes('ERR_NETWORK_CHANGED') ||
            networkErr.message?.includes('Network Error');
          
          if (isNetworkError && attempt < maxRetries) {
            const waitTime = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
            console.warn(`[Crawl Webpage] Network error (attempt ${attempt}/${maxRetries}), retrying in ${waitTime}ms...`, networkErr.message);
            setMessage(`Network error detected, retrying (${attempt}/${maxRetries})...`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
            continue;
          } else {
            throw networkErr;
          }
        }
      }
      
      setMessage(`Webpage ${webpageUrl} crawled (Scope: ${uploadScope}) and saved as ${response.data.file_name}`);
      await fetchUploadedFiles();
      
      // FORCE another refresh after 2 seconds to catch files that just started processing
      setTimeout(() => {
        console.log('Forcing file list refresh after single page crawl to catch processing files...');
        fetchUploadedFiles();
      }, 2000);
      
      setWebpageUrl('');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error occurred';
      setError(`Error crawling webpage: ${errorMessage}`);
    } finally {
      setIsCrawling(false);
      document.body.style.cursor = "default";
      setShowTeamContextModal(false);
    }
  };
  
  // NEW: State for crawl progress
  const [crawlProgress, setCrawlProgress] = useState(null); // {progress_percent, completed_urls, failed_urls, skipped_urls, total_urls}
  const [crawlBatchId, setCrawlBatchId] = useState(null); // Track current crawl batch ID

  // Sitemap crawl logic with async progress tracking
  const performSitemapCrawl = async () => {
    if (selectedUrls.length === 0) {
      setError('Please select at least one URL to crawl.');
      return;
    }

    setIsSitemapCrawling(true);
    setMessage('Initializing crawl job...'); 
    setError('');
    setCrawlProgress(null); // Reset progress
    document.body.style.cursor = "wait";
    setShowUrlSelectionModal(false); // Close the URL selection modal

    let finalCompanyId = null;
    let finalTeamId = null;
    if (uploadScope === 'company' && userCompany) finalCompanyId = userCompany.id;
    else if (uploadScope === 'team' && userCompany && selectedTeamId) {
      finalCompanyId = userCompany.id;
      finalTeamId = selectedTeamId;
    }

    try {
      const token = await getAuthToken();
      
      console.log('[Crawl] Starting async crawl job for', selectedUrls.length, 'URLs');
      
      const payload = { 
        selected_urls: selectedUrls,
        company_id: finalCompanyId,
        team_id: finalTeamId,
        ignore_robots_txt: ignoreRobotsTxt
      };
      
      // Start the crawl job with automatic retry for network errors
      let response;
      const maxRetries = 3;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          response = await axios.post(
            `${BACKEND_URL}/crawl_sitemap_selected`,
            payload,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              timeout: 30000
            }
          );
          break; // Success, exit retry loop
        } catch (networkErr) {
          const isNetworkError = 
            networkErr.code === 'ERR_NETWORK_CHANGED' ||
            networkErr.code === 'ECONNRESET' ||
            networkErr.code === 'ETIMEDOUT' ||
            networkErr.message?.includes('ERR_NETWORK_CHANGED') ||
            networkErr.message?.includes('Network Error');
          
          if (isNetworkError && attempt < maxRetries) {
            const waitTime = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Exponential backoff: 1s, 2s, 4s (max 5s)
            console.warn(`[Crawl] Network error (attempt ${attempt}/${maxRetries}), retrying in ${waitTime}ms...`, networkErr.message);
            setMessage(`Network error detected, retrying (${attempt}/${maxRetries})...`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
            continue; // Retry
          } else {
            throw networkErr; // Non-network error or max retries reached
          }
        }
      }

      console.log('[Crawl] Job created:', response.data);
      
      const { job_id, total_urls } = response.data;
      
      if (!job_id) {
        throw new Error('No job_id received from server');
      }
      
      setMessage(`Crawl job started for ${total_urls} URLs. Fetching progress...`);
      
      // Poll for progress every 3 seconds
      const pollInterval = setInterval(async () => {
        try {
          const progressResponse = await axios.get(
            `${BACKEND_URL}/crawl_progress/${job_id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              },
              timeout: 10000
            }
          );
          
          const progress = progressResponse.data;
          console.log('[Crawl Progress]', progress);
          
          // Update UI with current progress
          const { completed_urls, failed_urls, skipped_urls, total_urls, status, progress_percent, crawl_batch_id } = progress;
          
          // Store crawl_batch_id for later use
          if (crawl_batch_id && !crawlBatchId) {
            setCrawlBatchId(crawl_batch_id);
          }
          
          // Update progress state for visual progress bar
          setCrawlProgress({
            progress_percent: progress_percent || 0,
            completed_urls: completed_urls || 0,
            failed_urls: failed_urls || 0,
            skipped_urls: skipped_urls || 0,
            total_urls: total_urls || 0
          });
          
          setMessage(
            `Crawling ${total_urls} URLs (Scope: ${uploadScope}): ${progress_percent}% complete ` +
            `(✓ ${completed_urls} success, ✗ ${failed_urls} failed, ⊘ ${skipped_urls} skipped)`
          );
          
          // Check if job is complete
          if (status === 'completed' || status === 'failed') {
            clearInterval(pollInterval);
            
            // Check if file count limit was reached by analyzing failed_url_details
            let limitReached = false;
            let limitErrorMessage = '';
            if (progress.failed_url_details && progress.failed_url_details.length > 0) {
              const limitError = progress.failed_url_details.find(f => 
                f.error && f.error.includes('File count limit reached')
              );
              if (limitError) {
                limitReached = true;
                limitErrorMessage = limitError.error;
              }
            }
            
            // Final message
            let finalMessage = `Sitemap crawl ${status} (Scope: ${uploadScope}). `;
            finalMessage += `${completed_urls} of ${total_urls} URLs successfully processed.`;
            if (skipped_urls > 0) {
              finalMessage += ` ${skipped_urls} skipped.`;
            }
            if (failed_urls > 0) {
              finalMessage += ` ${failed_urls} failed.`;
              if (progress.failed_url_details && progress.failed_url_details.length > 0) {
                console.error('[Crawl] Failed URLs:', progress.failed_url_details);
              }
            }
            
            // Add limit warning if applicable
            if (limitReached) {
              const skippedCount = total_urls - completed_urls - failed_urls - skipped_urls;
              let limitWarning = `⚠️ Subscription File Limit Reached!\n\n`;
              limitWarning += `✓ Successfully crawled: ${completed_urls} of ${total_urls} URLs\n`;
              if (skippedCount > 0) {
                limitWarning += `⊘ Skipped due to file limit: ${skippedCount} URLs\n\n`;
              }
              limitWarning += `${limitErrorMessage}\n\n`;
              
              // Add actionable advice
              if (uploadConstraints.limitTotalFiles !== null) {
                limitWarning += `📊 Your subscription allows ${uploadConstraints.limitTotalFiles} total files.\n`;
                if (uploadTotals.currentFileCount !== null) {
                  limitWarning += `Current files: ${uploadTotals.currentFileCount + completed_urls}/${uploadConstraints.limitTotalFiles}\n\n`;
                }
              }
              
              limitWarning += `💡 To crawl more pages:\n`;
              limitWarning += `   • Delete old/unused files to free up space\n`;
              limitWarning += `   • Upgrade your subscription for a higher file limit\n`;
              limitWarning += `   • Contact support if you need assistance`;
              
              setError(limitWarning);
            }
            
            setMessage(finalMessage);
            setIsSitemapCrawling(false);
            document.body.style.cursor = "default";
            
            // Keep crawl progress visible for 3 seconds after completion
            setTimeout(() => {
              setCrawlProgress(null);
            }, 3000);
            
            // Refresh file list
            await fetchUploadedFiles();
            
            // NEW: Start processing progress tracking for the crawled files
            // They will need to be chunked and embedded
            if (completed_urls > 0 && crawl_batch_id) {
              console.log(`Starting processing progress for ${completed_urls} crawled files with batch ID: ${crawl_batch_id}`);
              // Pass the crawl_batch_id to track only files from this specific crawl
              startProgressPolling(null, crawl_batch_id);
            }
            
            // Clear the batch ID after starting tracking
            setCrawlBatchId(null);
            
            // Clear the URL input and selection
            setSitemapUrl('');
            setSelectedUrls([]);
            setSitemapUrlHierarchy({});
            setFlatUrls([]);
          }
        } catch (pollErr) {
          console.error('[Crawl] Error polling progress:', pollErr);
          // Continue polling unless it's a fatal error
          if (pollErr.response?.status === 404) {
            clearInterval(pollInterval);
            setError('Crawl job not found. It may have expired.');
            setIsSitemapCrawling(false);
            setCrawlProgress(null);
            document.body.style.cursor = "default";
          }
        }
      }, 3000); // Poll every 3 seconds
      
      // Safety timeout - stop polling after 30 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (isSitemapCrawling) {
          setError('Crawl job timed out. Please check the job status manually or contact support.');
          setIsSitemapCrawling(false);
          setCrawlProgress(null);
          document.body.style.cursor = "default";
        }
      }, 30 * 60 * 1000);
      
    } catch (err) {
      console.error('[Crawl] Error starting crawl job:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error occurred';
      setError(`Error starting crawl: ${errorMessage}`);
      setCrawlProgress(null);
    } finally {
      setIsSitemapCrawling(false);
      document.body.style.cursor = "default";
      setShowTeamContextModal(false);
    }
  };

  const handleModalConfirmScope = () => {
    if (uploadScope === 'team' && !selectedTeamId) {
        setModalError("Please select a team or choose 'Company-wide'.");
        return;
    }
    setModalError('');
    setShowTeamContextModal(false);

    if (actionToPerform === 'upload') {
        openFileDialog(); // This will trigger the hidden file input
    } else if (actionToPerform === 'crawl') {
        performCrawl();
    } else if (actionToPerform === 'sitemap_crawl'){
        performSitemapCrawl();
    } else if (actionToPerform === 'upload_url'){
        setShowUrlUploadDialog(true); // Show URL input dialog after team is selected
    }
  };

  const handleCreateTeamInModal = async () => {
    if (!newTeamNameInModal.trim()) {
      setModalError("Team name is required for new team.");
      return;
    }
    setLoadingCreateTeamInModal(true);
    setModalError('');
    setMessage(''); // Clear previous success messages from main page

    let token;
    try {
      token = await getAuthToken();
      if (!token) throw new Error("Authentication token not available. Please log in again.");

      const payload = { 
        team_name: newTeamNameInModal.trim(),
        description: newTeamDescriptionInModal.trim() || null 
      };

      const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;

      // Create the team
      const createTeamResponse = await fetch(`${BACKEND_URL}/functions/v1/onboarding-create-initial-team`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json',
          'apikey': anonKey
        },
        body: JSON.stringify(payload),
      });

      let createdTeamResult;
      if (!createTeamResponse.ok) {
        let errorResult = { error: `Failed to create team. Status: ${createTeamResponse.status}` }; // Default error
        try {
          errorResult = await createTeamResponse.json();
        } catch (e) {
          // Keep default error if parsing fails
          console.error("Could not parse error JSON from create team response", e);
        }
        throw new Error(errorResult.error || `Failed to create team. Status: ${createTeamResponse.status}`);
      }
      createdTeamResult = await createTeamResponse.json();
      
      // Don't set main page message here yet, do it after team list refresh
      // setMessage(`Team '${createdTeamResult.name}' created successfully! Refreshing team list...`);
      setNewTeamNameInModal('');
      setNewTeamDescriptionInModal('');
      
      // Refresh team list
      const getTeamsResponse = await fetch(`${BACKEND_URL}/functions/v1/get-company-teams`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'apikey': anonKey
        }
      });

      let updatedTeamsResult;
      if (!getTeamsResponse.ok) {
        let errorResult = { error: `Failed to refresh teams list. Status: ${getTeamsResponse.status}` };
        try {
          errorResult = await getTeamsResponse.json();
        } catch (e) {
          console.error("Could not parse error JSON from get teams response", e);
        }
        throw new Error(errorResult.error || `Failed to refresh teams list. Status: ${getTeamsResponse.status}`);
      }
      updatedTeamsResult = await getTeamsResponse.json();
      
      setCompanyTeams(updatedTeamsResult.teams || []);
      setSelectedTeamId(createdTeamResult.team_id); 
      setShowCreateTeamInModalForm(false);
      // Set success message inside the modal or a general one after everything is done
      setModalError(''); // Clear modal error on full success
      setMessage(`Team '${createdTeamResult.name}' created and selected!`); 

    } catch (err) {
      console.error("Error in handleCreateTeamInModal:", err);
      setModalError(err.message || "Could not complete team creation process.");
      // Main page error can also be set if desired, but modalError is more specific here
      // setError(err.message || "Could not complete team creation process."); 
    } finally {
      setLoadingCreateTeamInModal(false);
    }
  };

  // Cleanup duplicates and orphans (per-user) via backend endpoint
  const handleCleanup = async () => {
    setIsCleaning(true);
    setMessage('');
    setError('');
    document.body.style.cursor = 'wait';
    try {
      const token = await getAuthToken();
      const resp = await axios.post(
        `${BACKEND_URL}/cleanup_user_files`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000
        }
      );
      const deleted = Array.isArray(resp.data?.deleted_rows) ? resp.data.deleted_rows.length : 0;
      const removed = Array.isArray(resp.data?.removed_objects) ? resp.data.removed_objects.length : 0;
      setMessage(`Cleanup complete. Removed ${deleted} duplicate row(s) and ${removed} orphaned object(s).`);
      await fetchUploadedFiles();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error occurred';
      setError(`Cleanup failed: ${errorMessage}`);
    } finally {
      setIsCleaning(false);
      document.body.style.cursor = 'default';
    }
  };

  // --- NEW: Upload menu handlers ---
  const handleOpenMainMenu = (event) => {
    setUploadMenuAnchor(event.currentTarget);
  };

  const handleCloseMainMenu = () => {
    setUploadMenuAnchor(null);
    setUploadSubMenuAnchor(null);
    setCrawlSubMenuAnchor(null);
    setSelectedMainOption(null);
  };

  const handleMainMenuOptionClick = (option, event) => {
    setSelectedMainOption(option);
    if (option === 'upload') {
      setUploadSubMenuAnchor(event.currentTarget);
    } else if (option === 'crawl') {
      setCrawlSubMenuAnchor(event.currentTarget);
    }
  };

  const handleUploadFromComputer = () => {
    handleCloseMainMenu();
    handleInitiateUpload();
  };

  const handleUploadFromURL = () => {
    handleCloseMainMenu();
    setActionToPerform('upload_url');
    setShowTeamContextModal(true); // Show team selection first
  };

  const handleUrlUploadSubmit = async () => {
    if (!fileUrlInput || !fileUrlInput.trim()) {
      setError('Please enter a valid URL');
      return;
    }

    setIsUploading(true);
    setMessage('');
    setError('');
    setShowUrlUploadDialog(false);

    try {
      const token = await getAuthToken();
      
      // Download the file from URL on the backend
      const response = await axios.post(
        `${BACKEND_URL}/upload_from_url`,
        { 
          url: fileUrlInput,
          company_id: userCompany?.id || null,
          team_id: selectedTeamId || null
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          timeout: 180000 // 3 minute timeout
        }
      );

      setMessage(`File imported successfully from URL: ${response.data.filename}`);
      setFileUrlInput('');
      
      // Refresh file list
      fetchUploadedFiles();
      
      // Start progress tracking if file_id returned
      if (response.data.file_id) {
        startProgressPolling([response.data.file_id]);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to import file from URL';
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCrawlSinglePage = () => {
    handleCloseMainMenu();
    setShowCrawlContainer(true);
    // Scroll to crawler section
    setTimeout(() => {
      const crawlSection = document.querySelector('[data-crawl-section]');
      if (crawlSection) {
        crawlSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleCrawlSitemap = () => {
    handleCloseMainMenu();
    setShowCrawlContainer(true);
    // Scroll to crawler section
    setTimeout(() => {
      const crawlSection = document.querySelector('[data-crawl-section]');
      if (crawlSection) {
        crawlSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  // --- END NEW: Upload menu handlers ---

  // --- NEW: Reprocess file handler (re-embed uploaded files) ---
  const [isReprocessing, setIsReprocessing] = useState(false);
  
  const handleReprocessFile = async (fileId, filename, sourceUrl = null) => {
    // For crawled URLs, suggest using recrawl instead
    if (sourceUrl && sourceUrl.startsWith('http')) {
      setError('This is a crawled URL. Use the Recrawl button instead.');
      return;
    }
    
    // Confirmation for uploaded files
    if (!window.confirm(
      `Reprocess "${filename}"?\n\n` +
      `This will:\n` +
      `1. Verify file exists in storage\n` +
      `2. Delete old chunks and embeddings from Milvus\n` +
      `3. Re-process the file (chunk + embed)\n\n` +
      `Use this to fix embedding issues without re-uploading.\n\n` +
      `Continue?`
    )) {
      return;
    }

    setIsReprocessing(true);
    setMessage(`Reprocessing "${filename}"...`);
    setError('');
    
    try {
      const token = await getAuthToken();
      
      // Use the existing reprocess endpoint (without /api/)
      const response = await axios.post(
        `${BACKEND_URL}/reprocess_file/${fileId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000
        }
      );

      const data = response.data;
      
      setMessage(`✓ File "${filename}" queued for reprocessing. It will be re-chunked and re-embedded shortly.`);
      
      // Refresh file list after a short delay
      setTimeout(() => {
        fetchUploadedFiles();
      }, 2000);
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error';
      
      // Show user-friendly error messages
      if (errorMessage.includes('not found in storage')) {
        setError(`❌ ${errorMessage}\n\n💡 Tip: Delete this record and re-upload the file.`);
      } else if (errorMessage.includes('Storage path missing')) {
        setError(`❌ ${errorMessage}\n\n💡 Tip: This file record is corrupted. Please delete it and re-upload.`);
      } else if (errorMessage.includes('crawled URL')) {
        setError(`❌ ${errorMessage}`);
      } else {
        setError(`Failed to reprocess file: ${errorMessage}`);
      }
    } finally {
      setIsReprocessing(false);
    }
  };
  // --- END NEW: Reprocess file handler ---

  // --- NEW: File content viewer handler ---
  const handleViewFileContent = async (file) => {
    setSelectedFileForView(file);
    setLoadingFileContent(true);
    try {
      const { data, error } = await supabase
        .from('user_files')
        .select('extracted_full_text, searchable_summary, doc_summary')
        .eq('id', file.id)
        .single();
      
      if (error) throw error;
      
      setSelectedFileForView(prev => ({
        ...prev, 
        content: data?.extracted_full_text || 'No extracted text content available for this file.',
        searchable_summary: data?.searchable_summary,
        doc_summary: data?.doc_summary
      }));
    } catch (err) {
      setError(`Failed to load file content: ${err.message}`);
      setSelectedFileForView(prev => ({...prev, content: 'Error loading content.'}));
    } finally {
      setLoadingFileContent(false);
    }
  };
  // --- END NEW: File content viewer handler ---

  // --- NEW: Validation handlers ---
  const handleValidateSelected = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file to validate.');
      return;
    }

    setIsValidating(true);
    setMessage('');
    setError('');
    document.body.style.cursor = 'wait';

    try {
      const token = await getAuthToken();
      
      // Get file IDs from selected filenames
      const fileIds = uploadedFiles
        .filter(f => selectedFiles.includes(f.filename))
        .map(f => f.id)
        .filter(id => id);

      if (fileIds.length === 0) {
        setError('Could not find file IDs for selected files.');
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/validate_batch`,
        { file_ids: fileIds },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          timeout: 180000 // 3-minute timeout
        }
      );

      setValidationResults(response.data);
      
      // Debug logging
      console.log('Validation Results:', response.data);
      console.log('Problematic Files:', response.data.problematic_files);
      
      const { complete, incomplete, failed, success_rate } = response.data;
      let msg = `Validation complete: ${complete}/${fileIds.length} files are fully processed (${success_rate}% success rate).`;
      
      if (incomplete > 0 || failed > 0) {
        msg += ` ${incomplete} incomplete, ${failed} failed.`;
        
        if (response.data.problematic_files && response.data.problematic_files.length > 0) {
          const problemFiles = response.data.problematic_files.map(pf => pf.filename).join(', ');
          setError(`Problematic files: ${problemFiles}`);
        }
      }
      
      setMessage(msg);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error occurred';
      setError(`Validation failed: ${errorMessage}`);
    } finally {
      setIsValidating(false);
      document.body.style.cursor = 'default';
    }
  };
  // --- END NEW: Validation handlers ---

  // --- NEW: Recrawl selected handler ---
  const handleRecrawlSelected = async () => {
    // Filter: only crawled pages
    const crawledFiles = uploadedFiles.filter(f => 
      selectedFiles.includes(f.filename) && 
      f.source_url && 
      !f.source_url.startsWith(f.user_id || 'user_')
    );
    
    if (crawledFiles.length === 0) {
      setError('No crawled pages selected. Only web pages can be recrawled.');
      return;
    }
    
    if (!window.confirm(
      `Recrawl ${crawledFiles.length} selected page${crawledFiles.length !== 1 ? 's' : ''}?\n\n` +
      `This will:\n` +
      `1. Check each page for updates\n` +
      `2. Delete and re-crawl if content changed\n` +
      `3. Skip if content unchanged\n\n` +
      `Continue?`
    )) {
      return;
    }
    
    setIsRecrawling(true);
    setRecrawlProgress(0);
    setMessage('');
    setError('');
    
    const results = {
      unchanged: [],
      updated: [],
      failed: []
    };
    
    try {
      const token = await getAuthToken();
      
      // Process one by one
      for (let i = 0; i < crawledFiles.length; i++) {
        const file = crawledFiles[i];
        setRecrawlProgress(i + 1);
        setMessage(`Recrawling ${i + 1}/${crawledFiles.length}: ${file.source_url}`);
        
        try {
          // Use smart recrawl endpoint with header checking
          const response = await axios.post(
            `${BACKEND_URL}/api/smart_recrawl/${file.id}`,
            {},
            {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 120000
            }
          );
          
          // Track results more accurately
          if (response.data.status === 'unchanged') {
            results.unchanged.push(file.source_url);
          } else {
            results.updated.push(file.source_url);
          }
        } catch (err) {
          console.error(`Failed to recrawl ${file.source_url}:`, err);
          results.failed.push(file.source_url);
        }
        
        // Small delay between requests to avoid overwhelming server
        await delay(1000);
      }
      
      // Show summary
      let summary = `Recrawl complete!\n`;
      summary += `✓ ${results.updated.length} page${results.updated.length !== 1 ? 's' : ''} processed\n`;
      if (results.unchanged.length > 0) {
        summary += `→ ${results.unchanged.length} unchanged\n`;
      }
      if (results.failed.length > 0) {
        summary += `✗ ${results.failed.length} failed`;
        setError(`Failed to recrawl: ${results.failed.join(', ')}`);
      }
      
      setMessage(summary);
      
      // Clear selected files to show operation is complete
      setSelectedFiles([]);
      
      // Refresh file list after a delay to allow processing to start
      setTimeout(() => {
        fetchUploadedFiles();
      }, 3000);
      
    } catch (err) {
      console.error('Error during batch recrawl:', err);
      setError(`Batch recrawl error: ${err.message}`);
    } finally {
      setIsRecrawling(false);
      setRecrawlProgress(0);
    }
  };
  // --- END NEW ---

  // --- NEW: Single file smart recrawl handler ---
  const handleSingleRecrawl = async (fileId, sourceUrl, filename) => {
    if (!window.confirm(
      `Recrawl this page?\n\n${sourceUrl}\n\n` +
      `This will:\n` +
      `1. Check if the page has changed (via HTTP headers)\n` +
      `2. Skip if unchanged\n` +
      `3. Delete and re-crawl if changed\n\n` +
      `Continue?`
    )) {
      return;
    }
    
    setMessage(`Checking ${sourceUrl} for updates...`);
    
    try {
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${BACKEND_URL}/api/smart_recrawl/${fileId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000
        }
      );
      
      if (response.data.status === 'unchanged') {
        setMessage(
          `✓ ${filename} is up to date!\n` +
          `Checked via HTTP headers, no changes detected.`
        );
      } else {
        setMessage(
          `✓ ${filename} recrawled successfully!\n` +
          `Changes detected and content updated.`
        );
        
        // Refresh file list after 2 seconds to show new file
        setTimeout(() => {
          fetchUploadedFiles();
        }, 2000);
      }
      
    } catch (err) {
      console.error(`Failed to recrawl ${sourceUrl}:`, err);
      const errorMsg = err.response?.data?.detail || err.message;
      setError(`Failed to recrawl: ${errorMsg}`);
    }
  };
  // --- END NEW ---

  // --- NEW: Force Recrawl handler (bypasses change detection) ---
  const [isForceRecrawling, setIsForceRecrawling] = useState(false);
  
  const handleForceRecrawl = async (fileId, sourceUrl, filename) => {
    if (!window.confirm(
      `FORCE Recrawl "${filename}"?\n\n` +
      `URL: ${sourceUrl}\n\n` +
      `⚠️ This will:\n` +
      `1. Delete the existing file and all its data\n` +
      `2. Re-crawl the page from scratch\n` +
      `3. Re-chunk and re-embed everything\n\n` +
      `Use this when you know content has changed but smart recrawl didn't detect it.\n\n` +
      `Continue?`
    )) {
      return;
    }
    
    setIsForceRecrawling(true);
    setMessage(`Force recrawling ${sourceUrl}...`);
    setError('');
    
    try {
      const token = await getAuthToken();
      
      // Use dedicated force_recrawl endpoint
      const response = await axios.post(
        `${BACKEND_URL}/api/force_recrawl/${fileId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 180000 // 3 minutes for force recrawl
        }
      );
      
      // Check response status
      if (response.data.status === 'recrawled') {
        setMessage(
          `✓ ${filename} force recrawled successfully!\n` +
          `Content has been re-crawled, re-chunked, and re-embedded.`
        );
        // Refresh file list after 3 seconds to show new file
        setTimeout(() => {
          fetchUploadedFiles();
        }, 3000);
      } else if (response.data.status === 'unchanged') {
        setError(
          `⚠️ Force Recrawl failed to bypass change detection!\n\n` +
          `The backend still detected no changes. This might be a bug.\n` +
          `Response: ${JSON.stringify(response.data)}`
        );
      } else {
        setError(
          `Unexpected response from force recrawl:\n` +
          `Status: ${response.data.status}\n` +
          `Message: ${response.data.message || 'Unknown'}`
        );
      }
      
    } catch (err) {
      console.error(`Failed to force recrawl ${sourceUrl}:`, err);
      const errorMsg = err.response?.data?.detail || err.message;
      setError(`Failed to force recrawl: ${errorMsg}`);
    } finally {
      setIsForceRecrawling(false);
    }
  };
  // --- END Force Recrawl handler ---

  // --- NEW: Check All for Updates bulk handler ---
  const handleCheckAllForUpdates = async () => {
    const crawledFiles = uploadedFiles.filter(f => 
      f.source_url && !f.source_url.startsWith(f.user_id || 'user_')
    );
    
    if (crawledFiles.length === 0) {
      setError('No crawled pages to check');
      return;
    }
    
    if (!window.confirm(
      `Check ${crawledFiles.length} crawled pages for updates?\n\n` +
      `This will check HTTP headers only (fast, no recrawling).`
    )) {
      return;
    }
    
    setIsCheckingUpdates(true);
    setMessage(`Checking ${crawledFiles.length} pages for updates...`);
    
    const results = {
      unchanged: [],
      changed: [],
      error: []
    };
    
    try {
      const token = await getAuthToken();
      
      // Check all files (parallel for speed)
      const promises = crawledFiles.map(file => 
        axios.post(
          `${BACKEND_URL}/api/smart_recrawl/${file.id}`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 30000
          }
        ).then(response => ({
          file,
          status: response.data.status
        })).catch(err => ({
          file,
          status: 'error',
          error: err.message
        }))
      );
      
      const responses = await Promise.all(promises);
      
      // Categorize results
      responses.forEach(({ file, status }) => {
        if (status === 'unchanged') {
          results.unchanged.push(file.source_url);
        } else if (status === 'recrawled') {
          results.changed.push(file.source_url);
        } else {
          results.error.push(file.source_url);
        }
      });
      
      // Show summary
      let summary = `Update check complete!\n\n`;
      summary += `✓ ${results.unchanged.length} page${results.unchanged.length !== 1 ? 's' : ''} up to date\n`;
      
      if (results.changed.length > 0) {
        summary += `🔄 ${results.changed.length} page${results.changed.length !== 1 ? 's' : ''} need updating\n`;
        summary += `\nPages that need updates:\n`;
        results.changed.forEach(url => {
          summary += `  • ${url}\n`;
        });
        summary += `\nUse "Recrawl Selected" to update them.`;
      }
      
      if (results.error.length > 0) {
        summary += `\n❌ ${results.error.length} page${results.error.length !== 1 ? 's' : ''} had errors`;
      }
      
      setMessage(summary);
      
      // Refresh file list to show updated timestamps
      await fetchUploadedFiles();
      
    } catch (err) {
      console.error('Error checking for updates:', err);
      setError(`Failed to check for updates: ${err.message}`);
    } finally {
      setIsCheckingUpdates(false);
    }
  };
  // --- END NEW ---

  // --- NEW: Force Recrawl Selected bulk handler ---
  const handleForceRecrawlSelected = async () => {
    if (selectedFiles.length === 0) {
      setError('No files selected');
      return;
    }
    
    const selectedFileObjects = uploadedFiles.filter(f => selectedFiles.includes(f.filename));
    
    if (!window.confirm(
      `⚠️ FORCE RECRAWL ${selectedFileObjects.length} selected file${selectedFileObjects.length !== 1 ? 's' : ''}?\n\n` +
      `This will:\n` +
      `• Delete and re-crawl ALL selected pages/documents\n` +
      `• Re-chunk and re-embed ALL content\n` +
      `• Bypass ALL change detection\n\n` +
      `This operation takes time and cannot be undone!\n\n` +
      `Are you SURE you want to proceed?`
    )) {
      return;
    }
    
    setIsForceRecrawlingSelected(true);
    setMessage(`Force recrawling ${selectedFileObjects.length} file${selectedFileObjects.length !== 1 ? 's' : ''}...`);
    
    const results = {
      success: [],
      failed: []
    };
    
    try {
      const token = await getAuthToken();
      
      // Process files sequentially to avoid overloading the server
      for (let i = 0; i < selectedFileObjects.length; i++) {
        const file = selectedFileObjects[i];
        const progressMsg = `Force recrawling ${i + 1}/${selectedFileObjects.length}: ${file.filename}...`;
        setMessage(progressMsg);
        
        try {
          const response = await axios.post(
            `${BACKEND_URL}/api/force_recrawl/${file.id}`,
            {},
            {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 180000 // 3 minutes per file
            }
          );
          
          if (response.data.status === "recrawled") {
            results.success.push(file.filename);
          } else {
            results.failed.push({ filename: file.filename, reason: `Status: ${response.data.status}` });
          }
        } catch (err) {
          console.error(`Error force recrawling ${file.filename}:`, err);
          results.failed.push({ 
            filename: file.filename, 
            reason: err.response?.data?.detail || err.message 
          });
        }
      }
      
      // Show summary
      let summary = `Force recrawl complete!\n\n`;
      summary += `✓ ${results.success.length} file${results.success.length !== 1 ? 's' : ''} successfully recrawled\n`;
      
      if (results.failed.length > 0) {
        summary += `\n❌ ${results.failed.length} file${results.failed.length !== 1 ? 's' : ''} failed:\n`;
        results.failed.forEach(({ filename, reason }) => {
          summary += `  • ${filename}: ${reason}\n`;
        });
      }
      
      setMessage(summary);
      
      // Clear selection and refresh file list
      setSelectedFiles([]);
      await fetchUploadedFiles();
      
    } catch (err) {
      console.error('Error in bulk force recrawl:', err);
      setError(`Failed to force recrawl: ${err.message}`);
    } finally {
      setIsForceRecrawlingSelected(false);
    }
  };
  // --- END NEW ---

  return (
    <>
    <Container component="main" maxWidth={selectedFileForView ? "xl" : "lg"}>
      {/* Hidden file input */}
      <input 
        type="file"
        multiple
        onChange={handleFileSelectionForUpload} 
        accept=".pdf,.txt,.docx,.md,.html,.htm,.csv,.rtf,.json"
        style={{ display: 'none' }}
        ref={(input) => setFileInputRef(input)} 
      />

      <Box
        sx={{
          marginTop: 8,
          marginBottom: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5">Upload Document(s)</Typography>
        {uploadConstraints.limitTotalUploadMb !== null && uploadTotals.usedTotalUploadMb !== null && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Total storage used (files + crawls): {formatMb(uploadTotals.usedTotalUploadMb)} MB / {formatMb(uploadConstraints.limitTotalUploadMb)} MB
              {typeof uploadTotals.remainingTotalUploadMb === 'number' && ` (Remaining: ${formatMb(uploadTotals.remainingTotalUploadMb)} MB)`}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(
                100,
                (uploadTotals.usedTotalUploadMb / Math.max(uploadConstraints.limitTotalUploadMb, 0.000001)) * 100
              )}
            />
          </Box>
        )}

        {isUploading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Uploading...
            </Typography>
          </Box>
        )}

        {isDeleting && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Deleting {deletingFile}... ({deleteTimer}s)
            </Typography>
          </Box>
        )}

        {/* --- NEW: Modern Upload Interface --- */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<CloudUploadIcon />}
            onClick={handleOpenMainMenu}
            disabled={isUploading || isCrawling}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 3,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                boxShadow: 6,
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              },
              '&:disabled': {
                background: '#ccc',
              }
            }}
          >
            Add to Knowledge Base
          </Button>
          
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontStyle: 'italic' }}>
            💡 Import documents, websites, and other sources
          </Typography>

          {/* Main Menu */}
          <Menu
            anchorEl={uploadMenuAnchor}
            open={Boolean(uploadMenuAnchor)}
            onClose={handleCloseMainMenu}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 250,
                borderRadius: 2,
                boxShadow: 4,
              }
            }}
          >
            <MenuItem
              onClick={(e) => handleMainMenuOptionClick('upload', e)}
              sx={{ py: 1.5, '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' } }}
            >
              <ListItemIcon>
                <InsertDriveFileIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Upload Files"
                secondary="From computer or URL"
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </MenuItem>

            <MenuItem
              onClick={(e) => handleMainMenuOptionClick('crawl', e)}
              sx={{ py: 1.5, '&:hover': { bgcolor: 'secondary.light', color: 'secondary.contrastText' } }}
            >
              <ListItemIcon>
                <LanguageIcon fontSize="small" color="secondary" />
              </ListItemIcon>
              <ListItemText
                primary="Crawl Website"
                secondary="Extract content from web"
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </MenuItem>

            <MenuItem
              onClick={() => {
                setUploadMenuAnchor(null);
                setShowPlatformImportDialog(true);
              }}
              sx={{ py: 1.5, '&:hover': { bgcolor: 'success.light', color: 'success.contrastText' } }}
            >
              <ListItemIcon>
                <StorefrontIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Import from Store"
                secondary="E-commerce products"
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </MenuItem>
          </Menu>

          {/* Upload Submenu */}
          <Menu
            anchorEl={uploadSubMenuAnchor}
            open={Boolean(uploadSubMenuAnchor)}
            onClose={() => setUploadSubMenuAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                ml: 1,
                minWidth: 220,
                borderRadius: 2,
                boxShadow: 4,
              }
            }}
          >
            <MenuItem onClick={handleUploadFromComputer} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <CloudUploadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="From Computer" secondary="PDF, DOCX, TXT, etc." />
            </MenuItem>

            <MenuItem onClick={handleUploadFromURL} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <LinkIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="From URL" secondary="Direct file link" />
            </MenuItem>
          </Menu>

          {/* Crawl Submenu */}
          <Menu
            anchorEl={crawlSubMenuAnchor}
            open={Boolean(crawlSubMenuAnchor)}
            onClose={() => setCrawlSubMenuAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                ml: 1,
                minWidth: 220,
                borderRadius: 2,
                boxShadow: 4,
              }
            }}
          >
            <MenuItem onClick={handleCrawlSinglePage} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <WebIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Single Page" secondary="Crawl one URL" />
            </MenuItem>

            <MenuItem onClick={handleCrawlSitemap} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <MapIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Multiple Pages" secondary="From sitemap" />
            </MenuItem>
          </Menu>

          {files.length > 0 && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Selected Files:</Typography>
              {files.map((file, idx) => (
                <Typography key={idx} variant="body2">📄 {file.name}</Typography>
              ))}
            </Box>
          )}
        </Box>
        {/* --- END NEW: Modern Upload Interface --- */}

        {/* --- NEW: URL Upload Dialog --- */}
        <Dialog
          open={showUrlUploadDialog}
          onClose={() => setShowUrlUploadDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Upload File from URL</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Enter the direct URL of a file you want to import (e.g., PDF, DOCX, TXT).
              The file will be downloaded and added to your knowledge base.
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              label="File URL"
              placeholder="https://example.com/document.pdf"
              value={fileUrlInput}
              onChange={(e) => setFileUrlInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleUrlUploadSubmit();
                }
              }}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setShowUrlUploadDialog(false);
              setFileUrlInput('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleUrlUploadSubmit}
              variant="contained"
              disabled={isUploading || !fileUrlInput.trim()}
            >
              {isUploading ? <CircularProgress size={20} /> : 'Import File'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* --- END NEW: URL Upload Dialog --- */}

        {/* --- Platform Import Dialog --- */}
        <PlatformImportDialog
          open={showPlatformImportDialog}
          onClose={() => setShowPlatformImportDialog(false)}
          onImportComplete={() => {
            setShowPlatformImportDialog(false);
            fetchUploadedFiles(); // Refresh file list after import
          }}
          teamId={selectedTeamId || null}
        />
        {/* --- END Platform Import Dialog --- */}

        {/* Web Crawling Section - Hidden by default, shown when crawl option selected */}
        <Box 
          sx={{ 
            mt: 4, 
            p: { xs: 2, sm: 3 }, 
            border: '1px dashed #ccc', 
            borderRadius: 2, 
            display: showCrawlContainer ? 'block' : 'none',
            width: '100%',
            boxSizing: 'border-box'
          }}
          data-crawl-section
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              🌐 Crawl Web Content
            </Typography>
            <IconButton onClick={() => setShowCrawlContainer(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Extract content from websites and add it to your document collection.
          </Typography>
          
          {/* Collapsible Crawl Container */}
          {showCrawlContainer && (
            <Box 
              sx={{ 
                mt: 2, 
                p: { xs: 1.5, sm: 2 }, 
                border: '1px solid #eee', 
                borderRadius: 1,
                bgcolor: 'background.paper',
                transition: 'all 0.3s ease',
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'flex-start', 
                gap: 2 
              }}>
                <input
                  type="text"
                  value={webpageUrl}
                  onChange={handleWebpageUrlChange}
                  placeholder="Enter URL to crawl (e.g., https://example.com)"
                  style={{
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    flexGrow: 1,
                    fontSize: '16px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  disabled={isCrawling}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleInitiateCrawl}
                  disabled={isCrawling || !webpageUrl}
                  sx={{ 
                    height: '42px',
                    minWidth: { xs: '100%', sm: '160px' },
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {isCrawling ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      Crawling...
                    </>
                  ) : (
                    'Crawl Single Website'
                  )}
                </Button>
              </Box>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Enter a URL to extract content and add it to your document collection.
              </Typography>
              
              {/* Sitemap Crawling Section */}
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px dashed #ccc' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Crawl Website via Sitemap
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Extract URLs from a website's sitemap and select which pages to crawl.
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                  {/* URL Input */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'flex-start', 
                    gap: 2,
                    width: '100%'
                  }}>
                    <input
                      type="text"
                      value={sitemapUrl}
                      onChange={handleSitemapUrlChange}
                      placeholder="Enter website URL (e.g., https://example.com)"
                      style={{
                        padding: '10px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        width: '100%',
                        fontSize: '16px',
                        boxSizing: 'border-box'
                      }}
                      disabled={isLoadingSitemap || isSitemapCrawling}
                    />
                  </Box>
                  
                  {/* Robots.txt toggle */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1, width: '100%' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={ignoreRobotsTxt}
                          onChange={(e) => setIgnoreRobotsTxt(e.target.checked)}
                          disabled={isLoadingSitemap || isSitemapCrawling}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            Crawl all pages (ignore robots.txt restrictions)
                          </Typography>
                          <Tooltip 
                            title={
                              <Box>
                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                  <strong>Unchecked (default):</strong> Respects robots.txt rules and only shows URLs allowed for crawling
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>Checked:</strong> Shows and crawls all URLs, ignoring robots.txt restrictions
                                </Typography>
                              </Box>
                            }
                            arrow
                          >
                            <Typography variant="caption" sx={{ cursor: 'help', color: 'info.main' }}>
                              ℹ️
                            </Typography>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </Box>

                  {/* Extract URLs Button */}
                  <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mt: 1, width: '100%' }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={smartSitemapExtraction}
                      disabled={isLoadingSitemap || isSitemapCrawling}
                      sx={{ 
                        height: '42px',
                        minWidth: { xs: '100%', sm: '200px' },
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}
                    >
                      {isLoadingSitemap ? (
                        <>
                          <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                          Processing Sitemap...
                        </>
                      ) : (
                        'Extract Sitemap URLs'
                      )}
                    </Button>
                  </Box>
                  
                  {(isLoadingSitemap || isSitemapCrawling) && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        {isLoadingSitemap ? 'Extracting URLs from sitemap...' : 'Crawling selected URLs...'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* --- NEW: Crawl Progress Bar --- */}
        {crawlProgress && (
          <Box
            sx={{
              mt: 3,
              mb: 2,
              p: 2,
              bgcolor: 'background.paper',
              boxShadow: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'secondary.light'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="secondary">
                🌐 Crawling URLs
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {crawlProgress.completed_urls + crawlProgress.failed_urls}/{crawlProgress.total_urls}
                </Typography>
              </Box>
            </Box>

            <LinearProgress 
              variant="determinate" 
              value={crawlProgress.progress_percent || 0}
              color="secondary"
              sx={{ mb: 1, height: 10, borderRadius: 5 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {crawlProgress.progress_percent || 0}% complete
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="success.main">
                  ✓ {crawlProgress.completed_urls} success
                </Typography>
                {crawlProgress.failed_urls > 0 && (
                  <Typography variant="body2" color="error.main">
                    ✗ {crawlProgress.failed_urls} failed
                  </Typography>
                )}
                {crawlProgress.skipped_urls > 0 && (
                  <Typography variant="body2" color="warning.main">
                    ⊘ {crawlProgress.skipped_urls} skipped
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
        {/* --- END NEW: Crawl Progress Bar --- */}

        {/* --- NEW: Upload/Processing Progress Tracking Box --- */}
        {showProgressSnackbar && uploadProgress && (
          <Box
            sx={{
              mt: 3,
              mb: 2,
              p: 2,
              bgcolor: 'background.paper',
              boxShadow: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.light'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                📄 Processing Files
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {uploadProgress.completed + uploadProgress.failed}/{uploadProgress.total}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setShowProgressSnackbar(false);
                    stopProgressPolling();
                  }}
                  sx={{ ml: 1 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <LinearProgress 
              variant="determinate" 
              value={uploadProgress.overall_progress || 0}
              sx={{ mb: 1, height: 10, borderRadius: 5 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {uploadProgress.overall_progress || 0}% complete
              </Typography>
              {uploadProgress.processing > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircularProgress size={14} />
                  <Typography variant="body2" color="primary">
                    {uploadProgress.processing} processing
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="success.main" fontWeight="bold">
                  ✓ All done!
                </Typography>
              )}
            </Box>

            {/* Show breakdown by status */}
            {uploadProgress.files && uploadProgress.files.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(() => {
                  const statusCounts = {};
                  uploadProgress.files.forEach(f => {
                    const status = f.status || 'unknown';
                    statusCounts[status] = (statusCounts[status] || 0) + 1;
                  });
                  return Object.entries(statusCounts).map(([status, count]) => {
                    let color = 'text.secondary';
                    let icon = '⏳';
                    if (status === 'completed') { color = 'success.main'; icon = '✓'; }
                    else if (status === 'failed') { color = 'error.main'; icon = '✗'; }
                    else if (status === 'chunking') { color = 'info.main'; icon = '📦'; }
                    else if (status === 'embedding') { color = 'warning.main'; icon = '🔢'; }
                    else if (status === 'pending') { color = 'text.secondary'; icon = '⏳'; }
                    
                    return (
                      <Typography 
                        key={status} 
                        variant="caption" 
                        sx={{ 
                          color, 
                          bgcolor: 'rgba(0,0,0,0.04)', 
                          px: 1, 
                          py: 0.5, 
                          borderRadius: 1,
                          fontWeight: 'medium'
                        }}
                      >
                        {icon} {count} {status}
                      </Typography>
                    );
                  });
                })()}
              </Box>
            )}

            {uploadProgress.failed > 0 && (
              <Typography variant="body2" color="error" sx={{ mt: 1, display: 'block' }}>
                ⚠ {uploadProgress.failed} file{uploadProgress.failed > 1 ? 's' : ''} failed
              </Typography>
            )}
          </Box>
        )}
        {/* --- END NEW: Upload/Processing Progress Tracking Box --- */}

        {/* --- NEW: Side-by-side layout for file list and content viewer --- */}
        <Grid container spacing={3} sx={{ mt: 4, width: '100%', maxWidth: selectedFileForView ? '100%' : '1400px', mx: 'auto' }}>
          {/* File List (Left Side) */}
          <Grid item xs={12} lg={selectedFileForView ? 6 : 12}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6">Your Uploaded Files</Typography>
              </Box>

              {/* --- NEW: Search and Filter Bar --- */}
              {uploadedFiles.length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* Search Bar */}
                  <TextField
                    size="small"
                    placeholder="Search URLs and filenames..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ flexGrow: 1, minWidth: '300px' }}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                          🔍
                        </Box>
                      ),
                      endAdornment: searchQuery && (
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery('')}
                          sx={{ p: 0.5 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )
                    }}
                  />

                  {/* Filter Dropdown */}
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filter</InputLabel>
                    <Select
                      value={filterType}
                      label="Filter"
                      onChange={(e) => {
                        setFilterType(e.target.value);
                        setPage(0); // Reset to first page when filter changes
                      }}
                    >
                      <MenuItem value="all">All Files</MenuItem>
                      <MenuItem value="crawled">Crawled Pages</MenuItem>
                      <MenuItem value="uploaded">Uploaded Files</MenuItem>
                      <MenuItem value="personal">Personal</MenuItem>
                      {userCompany && <MenuItem value="company">Company: {userCompany.name}</MenuItem>}
                      {companyTeams && companyTeams.length > 0 && <Divider />}
                      {companyTeams && companyTeams.map((team) => (
                        <MenuItem key={team.team_id} value={team.team_id}>
                          Team: {team.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Results count */}
                  <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {filteredAndSearchedFiles.length === uploadedFiles.length 
                      ? `${uploadedFiles.length} file${uploadedFiles.length !== 1 ? 's' : ''}`
                      : `${filteredAndSearchedFiles.length} of ${uploadedFiles.length} file${uploadedFiles.length !== 1 ? 's' : ''}`
                    }
                  </Typography>
                </Box>
              )}
              {/* --- END NEW: Search and Filter Bar --- */}

              {uploadedFiles.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allOnPageSelected}
                      indeterminate={someOnPageSelected}
                      onChange={handleSelectAllFiles}
                      disabled={visibleFiles.length === 0}
                    />
                  }
                  label="Select All"
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={handleValidateSelected}
                  disabled={isValidating || selectedFiles.length === 0}
                  startIcon={isValidating ? <CircularProgress size={16} /> : null}
                >
                  {isValidating ? 'Validating...' : `Validate (${selectedFiles.length})`}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleCleanup}
                  disabled={isCleaning}
                  startIcon={isCleaning ? <CircularProgress size={16} /> : null}
                >
                  {isCleaning ? 'Cleaning...' : 'Cleanup'}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={handleRecrawlSelected}
                  disabled={isRecrawling || (() => {
                    const crawledSelected = uploadedFiles.filter(f => 
                      selectedFiles.includes(f.filename) && 
                      f.source_url && 
                      !f.source_url.startsWith(f.user_id || 'user_')
                    );
                    return crawledSelected.length === 0;
                  })()}
                  startIcon={isRecrawling ? <CircularProgress size={16} /> : <RefreshIcon />}
                >
                  {isRecrawling ? `Recrawling... (${recrawlProgress})` : (() => {
                    const crawledSelected = uploadedFiles.filter(f => 
                      selectedFiles.includes(f.filename) && 
                      f.source_url && 
                      !f.source_url.startsWith(f.user_id || 'user_')
                    );
                    return `Recrawl (${crawledSelected.length})`;
                  })()}
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={handleCheckAllForUpdates}
                  disabled={isCheckingUpdates || (() => {
                    const crawledFiles = uploadedFiles.filter(f => 
                      f.source_url && !f.source_url.startsWith(f.user_id || 'user_')
                    );
                    return crawledFiles.length === 0;
                  })()}
                  startIcon={isCheckingUpdates ? <CircularProgress size={16} /> : <RefreshIcon />}
                >
                  {isCheckingUpdates ? 'Checking...' : 'Check All for Updates'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleForceRecrawlSelected}
                  disabled={isForceRecrawlingSelected || selectedFiles.length === 0}
                  startIcon={isForceRecrawlingSelected ? <CircularProgress size={16} /> : <RestartAltIcon />}
                  sx={{ 
                    color: '#d32f2f',
                    borderColor: '#d32f2f',
                    '&:hover': { 
                      borderColor: '#b71c1c',
                      bgcolor: '#ffcdd2' 
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(211, 47, 47, 0.3)',
                      borderColor: 'rgba(211, 47, 47, 0.3)'
                    }
                  }}
                >
                  {isForceRecrawlingSelected ? 'Force Recrawling...' : `Force Recrawl (${selectedFiles.length})`}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleDelete}
                  disabled={isDeleting || selectedFiles.length === 0}
                  startIcon={isDeleting ? <CircularProgress size={16} /> : null}
                >
                  {isDeleting ? `Deleting... (${deleteTimer}s)` : `Delete (${selectedFiles.length})`}
                </Button>
              </Box>
            )}
          
          <TableContainer component={Paper} sx={{ 
            mt: 2, 
            overflowX: 'auto', 
            maxWidth: '100%',
            // On mobile, remove padding to maximize space
            '& .MuiPaper-root': {
              px: { xs: 0, sm: 2 }
            }
          }}>
            <Table aria-label="uploaded files table" sx={{ 
              // Remove fixed layout on small screens, use auto on desktop
              tableLayout: { xs: 'auto', md: 'fixed' },
              width: '100%',
              minWidth: { xs: '100%', sm: '800px' } // Allow horizontal scroll on small screens
            }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: { xs: '32px', sm: '40px' }, px: { xs: 0.5, sm: 1 } }}>
                    <Checkbox
                      checked={allOnPageSelected}
                      indeterminate={someOnPageSelected}
                      onChange={handleSelectAllFiles}
                      disabled={visibleFiles.length === 0}
                      size="small"
                    />
                  </TableCell>
                  <TableCell 
                    key="filename" 
                    sortDirection={orderBy === 'filename' ? order : false} 
                    sx={{ 
                      width: { xs: 'auto', md: '18%' }, 
                      minWidth: { xs: '120px', sm: '150px' },
                      px: { xs: 0.5, sm: 2 }
                    }}
                  >
                    <TableSortLabel active={orderBy === 'filename'} direction={orderBy === 'filename' ? order : 'asc'} onClick={(e) => handleRequestSort(e, 'filename')}>
                       Filename {orderBy === 'filename' ? (<Box component="span" sx={visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box>) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    key="processing_status" 
                    sortDirection={orderBy === 'processing_status' ? order : false} 
                    sx={{ 
                      width: { xs: 'auto', md: '8%' }, 
                      minWidth: { xs: '60px', sm: '80px' },
                      display: { xs: 'none', sm: 'table-cell' }, // Hide on mobile
                      px: { xs: 0.5, sm: 2 }
                    }}
                  >
                     <TableSortLabel active={orderBy === 'processing_status'} direction={orderBy === 'processing_status' ? order : 'asc'} onClick={(e) => handleRequestSort(e, 'processing_status')}>
                         Status {orderBy === 'processing_status' ? (<Box component="span" sx={visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box>) : null}
                     </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    key="file_size" 
                    sortDirection={orderBy === 'size' ? order : false} 
                    sx={{ 
                      width: { xs: 'auto', md: '8%' }, 
                      minWidth: { xs: '50px', sm: '70px' },
                      display: { xs: 'none', md: 'table-cell' }, // Hide on mobile & tablet
                      px: { xs: 0.5, sm: 2 }
                    }}
                  >
                    <TableSortLabel active={orderBy === 'size'} direction={orderBy === 'size' ? order : 'asc'} onClick={(e) => handleRequestSort(e, 'size')}>
                      Size {orderBy === 'size' ? (<Box component="span" sx={visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box>) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    key="uploaded_date" 
                    sortDirection={orderBy === 'created_at' ? order : false} 
                    sx={{ 
                      width: { xs: 'auto', md: '12%' }, 
                      minWidth: { xs: '80px', sm: '120px' },
                      display: { xs: 'none', md: 'table-cell' }, // Hide on mobile & tablet
                      px: { xs: 0.5, sm: 2 }
                    }}
                  >
                    <TableSortLabel active={orderBy === 'created_at'} direction={orderBy === 'created_at' ? order : 'asc'} onClick={(e) => handleRequestSort(e, 'created_at')}>
                      Uploaded {orderBy === 'created_at' ? (<Box component="span" sx={visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box>) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    key="company_name" 
                    sortDirection={orderBy === 'company_name' ? order : false} 
                    sx={{ 
                      width: { xs: 'auto', md: '10%' }, 
                      minWidth: { xs: '80px', sm: '100px' },
                      display: { xs: 'none', lg: 'table-cell' }, // Hide on mobile, tablet & small desktop
                      px: { xs: 0.5, sm: 2 }
                    }}
                  >
                    <TableSortLabel active={orderBy === 'company_name'} direction={orderBy === 'company_name' ? order : 'asc'} onClick={(e) => handleRequestSort(e, 'company_name')}>
                      Company {orderBy === 'company_name' ? (<Box component="span" sx={visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box>) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    key="team_name" 
                    sortDirection={orderBy === 'team_name' ? order : false} 
                    sx={{ 
                      width: { xs: 'auto', md: '10%' }, 
                      minWidth: { xs: '70px', sm: '100px' },
                      display: { xs: 'none', lg: 'table-cell' }, // Hide on mobile, tablet & small desktop
                      px: { xs: 0.5, sm: 2 }
                    }}
                  >
                    <TableSortLabel active={orderBy === 'team_name'} direction={orderBy === 'team_name' ? order : 'asc'} onClick={(e) => handleRequestSort(e, 'team_name')}>
                      Team {orderBy === 'team_name' ? (<Box component="span" sx={visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box>) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    key="last_updated" 
                    sortDirection={orderBy === 'last_modified' ? order : false} 
                    sx={{ 
                      width: { xs: 'auto', md: '12%' }, 
                      minWidth: { xs: '70px', sm: '120px' },
                      display: { xs: 'none', sm: 'table-cell' }, // Hide on mobile
                      px: { xs: 0.5, sm: 2 }
                    }}
                  >
                    <TableSortLabel active={orderBy === 'last_modified'} direction={orderBy === 'last_modified' ? order : 'asc'} onClick={(e) => handleRequestSort(e, 'last_modified')}>
                      Last Updated {orderBy === 'last_modified' ? (<Box component="span" sx={visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box>) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    key="actions" 
                    sx={{ 
                      width: { xs: 'auto', md: '110px' }, 
                      minWidth: { xs: '90px', sm: '110px' },
                      px: { xs: 0.5, sm: 2 }
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleFiles.length > 0 ? (
                  visibleFiles.map((file, index) => { // Map over visibleFiles
                    const isSelected = selectedFiles.includes(file.filename);
                    // ... Determine status display ...
                    let statusDisplay = file.processing_status || "Ready";
                    let statusColor = "success.main";
                    let isReady = statusDisplay === 'completed' || statusDisplay === 'Ready';
                    // Simple status mapping - adjust as needed
                    if (statusDisplay === 'processing' || statusDisplay === 'pending' || statusDisplay === 'chunking' || statusDisplay === 'embedding') {
                         statusColor = "info.main"; isReady = false;
                    } else if (statusDisplay === 'error' || statusDisplay === 'failed') {
                         statusColor = "error.main"; isReady = false;
                    } else if (statusDisplay === 'unknown') {
                         statusColor = "text.secondary"; isReady = false;
                    }

                    return (
                      <TableRow key={file.filename || index}>
                        <TableCell padding="checkbox" sx={{ px: { xs: 0.5, sm: 1 } }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={(event) => handleFileSelection(file.filename, event.target.checked)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ px: { xs: 0.5, sm: 2 } }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {/* Show URL for crawled pages, filename for uploaded files */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ 
                                wordBreak: 'break-word',
                                color: (statusDisplay === 'failed' || statusDisplay === 'error') ? 'error.main' : 
                                       (validationResults && validationResults.problematic_files && 
                                        validationResults.problematic_files.some(pf => pf.file_id === file.id)) ? 'warning.main' : 'text.primary'
                              }}>
                                {/* If source_url exists and doesn't start with user_id (storage path), it's a crawled page */}
                                {file.source_url && !file.source_url.startsWith(file.user_id || 'user_') ? (
                                  <>
                                    <LinkIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle', color: 'primary.main' }} />
                                    {file.source_url}
                                  </>
                                ) : (
                                  <>
                                    <DescriptionIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />
                                    {file.filename}
                                  </>
                                )}
                              </Typography>
                              
                              {/* NEW: Show Refresh button for problematic files */}
                              {(file.status === 'failed' || file.processing_status === 'error' ||
                                (validationResults && validationResults.problematic_files && 
                                 validationResults.problematic_files.some(pf => pf.file_id === file.id))
                              ) && (
                                <Tooltip title={file.source_url ? "Re-crawl this page" : "Reprocess this file"}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleReprocessFile(file.id, file.filename, file.source_url)}
                                    sx={{ 
                                      color: 'warning.main',
                                      bgcolor: 'warning.light',
                                      '&:hover': { bgcolor: 'warning.main', color: 'white' },
                                      p: 0.5
                                    }}
                                  >
                                    <RefreshIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              
                              {/* Show copy URL button if source_url exists and file is failed */}
                              {(statusDisplay === 'failed' || statusDisplay === 'error') && file.source_url && (
                                <Tooltip title="Copy URL to crawl again">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      navigator.clipboard.writeText(file.source_url);
                                      setMessage(`URL copied: ${file.source_url}`);
                                      setTimeout(() => setMessage(''), 3000);
                                    }}
                                    sx={{ 
                                      minWidth: 'auto',
                                      px: 0.5,
                                      py: 0.25,
                                      fontSize: '0.65rem',
                                      borderColor: 'error.main',
                                      color: 'error.main',
                                      '&:hover': {
                                        borderColor: 'error.dark',
                                        bgcolor: '#ffebee'
                                      }
                                    }}
                                  >
                                    📋 Copy URL
                                  </Button>
                                </Tooltip>
                              )}
                            </Box>
                            
                            {/* Show original filename as secondary info for crawled pages */}
                            {file.source_url && !file.source_url.startsWith(file.user_id || 'user_') && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', ml: 2.5 }}>
                                File: {file.filename}
                              </Typography>
                            )}
                            
                            {/* Show last checked timestamp for crawled pages */}
                            {file.source_url && !file.source_url.startsWith(file.user_id || 'user_') && 
                             file.processing_metadata?.last_checked && (
                              <Typography variant="caption" sx={{ color: 'info.main', fontSize: '0.65rem', ml: 2.5 }}>
                                ✓ Checked: {new Date(file.processing_metadata.last_checked).toLocaleString()}
                              </Typography>
                            )}
                            
                            {/* Show warning for outdated pages (not checked in 6+ months) */}
                            {(() => {
                              // Helper to check if a page is outdated
                              if (!file.source_url || file.source_url.startsWith(file.user_id || 'user_')) {
                                return null; // Not a crawled page
                              }
                              
                              const lastChecked = file.processing_metadata?.last_checked || file.created_at;
                              if (!lastChecked) return null;
                              
                              const daysSinceCheck = (Date.now() - new Date(lastChecked)) / (1000 * 60 * 60 * 24);
                              const isOutdated = daysSinceCheck > 180; // Older than 6 months (180 days)
                              
                              if (!isOutdated) return null;
                              
                              const monthsSinceCheck = Math.floor(daysSinceCheck / 30);
                              
                              return (
                                <Tooltip 
                                  title={
                                    <Box sx={{ p: 0.5 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        ⚠️ Content May Be Outdated
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        This page was last crawled {monthsSinceCheck} month{monthsSinceCheck !== 1 ? 's' : ''} ago.
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                        The content may have changed on the website since then.
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                                        → Click the 🔄 button to check for updates and recrawl if needed.
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                  placement="right"
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      bgcolor: 'warning.light',
                                      color: 'warning.dark',
                                      px: 0.75,
                                      py: 0.25,
                                      borderRadius: 0.5,
                                      fontSize: '0.65rem',
                                      fontWeight: 'bold',
                                      ml: 2.5,
                                      display: 'inline-block',
                                      cursor: 'help'
                                    }}
                                  >
                                    ⚠️ MAY BE OUTDATED
                                  </Typography>
                                </Tooltip>
                              );
                            })()}
                            
                            {/* Show failed warning badge if status is failed */}
                            {(statusDisplay === 'failed' || statusDisplay === 'error') && (
                              <Tooltip 
                                title={
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                      ⚠️ Processing Failed
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                      This file failed to process after multiple attempts.
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                      <strong>Action:</strong> Delete this file and try again.
                                    </Typography>
                                    {file.source_url && (
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                        Use "Copy URL" button above to re-crawl this page.
                                      </Typography>
                                    )}
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                      If the problem persists, the content may be inaccessible or in an unsupported format.
                                    </Typography>
                                  </Box>
                                }
                                placement="right"
                                arrow
                              >
                                <Box sx={{ 
                                  display: 'inline-flex', 
                                  alignItems: 'center',
                                  cursor: 'help',
                                  bgcolor: '#ffebee',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  border: '1px solid #ef5350',
                                  width: 'fit-content'
                                }}>
                                  <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold', mr: 0.5 }}>
                                    ⚠️
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'medium' }}>
                                    {statusDisplay}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, px: { xs: 0.5, sm: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {!isReady && statusDisplay !== 'failed' && statusDisplay !== 'error' && <CircularProgress size={16} sx={{ mr: 1 }} />}
                            <Typography variant="body2" sx={{ color: statusColor, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {statusDisplay}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: '0.875rem', px: { xs: 0.5, sm: 2 } }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                            {typeof file.size === 'number' ? 
                              (file.size >= 1024*1024 ? `${(file.size/(1024*1024)).toFixed(2)} MB` : 
                               file.size >= 1024 ? `${(file.size/1024).toFixed(1)} KB` : `${file.size} B`) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: '0.875rem', px: { xs: 0.5, sm: 2 } }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                            {file.created_at ? new Date(file.created_at).toLocaleString('en-US', { 
                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                            }) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: '0.875rem', px: { xs: 0.5, sm: 2 } }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.8rem' }, 
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.company_name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: '0.875rem', px: { xs: 0.5, sm: 2 } }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '0.8rem' }, 
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.team_name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: '0.875rem', px: { xs: 0.5, sm: 2 } }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                            {file.last_modified ? new Date(file.last_modified).toLocaleString('en-US', { 
                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                            }) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ p: { xs: 0.25, sm: 0.5 }, px: { xs: 0.25, sm: 1 } }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: { xs: 0.1, sm: 0.2 }, 
                            flexWrap: 'nowrap', 
                            justifyContent: 'flex-start' 
                          }}>
                            {/* View Content Button */}
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => handleViewFileContent(file)}
                                sx={{ 
                                  color: 'primary.main', 
                                  p: { xs: 0.15, sm: 0.25 }, 
                                  minWidth: 0 
                                }}
                              >
                                <VisibilityIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                              </IconButton>
                            </Tooltip>
                            
                            {/* Smart Recrawl button for crawled pages (URLs starting with http) */}
                            {file.source_url && file.source_url.startsWith('http') && (
                              <Tooltip title="Smart Recrawl (checks for changes first)">
                                <IconButton
                                  size="small"
                                  onClick={() => handleSingleRecrawl(file.id, file.source_url, file.filename)}
                                  sx={{ 
                                    color: 'secondary.main',
                                    '&:hover': { bgcolor: 'secondary.light' },
                                    p: { xs: 0.15, sm: 0.25 },
                                    minWidth: 0
                                  }}
                                >
                                  <RefreshIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {/* Force Recrawl button for crawled pages (bypasses change detection) */}
                            {file.source_url && file.source_url.startsWith('http') && (
                              <Tooltip title="⚠️ FORCE Recrawl - bypasses change detection!">
                                <IconButton
                                  size="small"
                                  onClick={() => handleForceRecrawl(file.id, file.source_url, file.filename)}
                                  disabled={isForceRecrawling}
                                  sx={{ 
                                    color: '#fff',
                                    bgcolor: '#d32f2f',
                                    '&:hover': { bgcolor: '#b71c1c' },
                                    p: { xs: 0.25, sm: 0.35 },
                                    minWidth: 0,
                                    ml: 0.5
                                  }}
                                >
                                  <RestartAltIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {/* Reprocess button for uploaded files (not crawled URLs) */}
                            {(!file.source_url || !file.source_url.startsWith('http')) && (
                              <Tooltip title="Reprocess (re-embed)">
                                <IconButton
                                  size="small"
                                  onClick={() => handleReprocessFile(file.id, file.filename)}
                                  disabled={isReprocessing}
                                  sx={{ 
                                    color: 'warning.main',
                                    '&:hover': { bgcolor: 'warning.light' },
                                    p: { xs: 0.15, sm: 0.25 },
                                    minWidth: 0
                                  }}
                                >
                                  <RefreshIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {/* Open URL for crawled pages */}
                            {file.source_url && (
                              <Tooltip title="Open">
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={file.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ 
                                    color: 'info.main', 
                                    p: { xs: 0.15, sm: 0.25 }, 
                                    minWidth: 0 
                                  }}
                                >
                                  <OpenInNewIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {/* Open/Download document for uploaded files */}
                            {!file.source_url && file.storage_path && (
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    try {
                                      const { data, error } = await supabase.storage
                                        .from('user_uploads')
                                        .download(file.storage_path);
                                      
                                      if (error) throw error;
                                      
                                      // Create download link
                                      const url = window.URL.createObjectURL(data);
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.download = file.filename;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      window.URL.revokeObjectURL(url);
                                    } catch (err) {
                                      setError(`Failed to download file: ${err.message}`);
                                    }
                                  }}
                                  sx={{ color: 'success.main', p: 0.25, minWidth: 0 }}
                                >
                                  <DescriptionIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {/* Reprocess file button - show for failed files OR files that failed validation */}
                            {(() => {
                              const isFailedFile = file.status === 'failed' || file.processing_status === 'error';
                              const isProblematicFile = validationResults && 
                                validationResults.problematic_files && 
                                validationResults.problematic_files.some(pf => pf.file_id === file.id);
                              
                              // Debug logging (will show in browser console)
                              if (validationResults && (file.filename.includes('policy') || file.filename.includes('equity'))) {
                                console.log(`File: ${file.filename}, ID: ${file.id}, isProblematic: ${isProblematicFile}`, validationResults.problematic_files);
                              }
                              
                              return (isFailedFile || isProblematicFile) ? (
                                <Tooltip title="Reprocess">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleReprocessFile(file.id, file.filename, file.source_url)}
                                    sx={{ color: 'warning.main', p: 0.25, minWidth: 0 }}
                                  >
                                    <RefreshIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              ) : null;
                            })()}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">No files to display.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {uploadedFiles.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredAndSearchedFiles.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ mt: 1, borderTop: '1px solid rgba(224, 224, 224, 1)' }}
            />
          )}
            </Box>
          </Grid>
          
          {/* File Content Viewer (Right Side) */}
          {selectedFileForView && (
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 3, height: '80vh', overflow: 'auto', position: 'sticky', top: 20 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    File Content: {selectedFileForView.filename}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setSelectedFileForView(null)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                
                {loadingFileContent ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/* Searchable Summary Box */}
                    {selectedFileForView.searchable_summary && (
                      <Paper sx={{ p: 2, mb: 2, bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                          Searchable Summary (for RAG):
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                          {selectedFileForView.searchable_summary}
                        </Typography>
                      </Paper>
                    )}
                    
                    {/* General Summary Box */}
                    {selectedFileForView.doc_summary && (
                      <Paper sx={{ p: 2, mb: 2, bgcolor: '#fff3e0', border: '1px solid #ffb74d' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#e65100' }}>
                          Summary (general):
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                          {selectedFileForView.doc_summary}
                        </Typography>
                      </Paper>
                    )}
                    
                    {/* File Content Box */}
                    <Paper sx={{ p: 2, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        File Content (full text):
                      </Typography>
                      <TextField
                        multiline
                        fullWidth
                        rows={25}
                        value={selectedFileForView.content || ''}
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': { 
                            height: '100%', 
                            alignItems: 'flex-start',
                            bgcolor: 'white'
                          },
                          '& .MuiOutlinedInput-input': { 
                            height: '100% !important', 
                            overflow: 'auto !important',
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    </Paper>
                  </>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
        {/* --- END NEW: Side-by-side layout --- */}

        <Box sx={{ mt: 4, width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleChatNavigation}
            disabled={uploadedFiles.length === 0 || !uploadedFiles.some(file => 
              file.processing_status === 'completed' || !file.processing_status
            )}
          >
            Start Chat with your Documents
          </Button>
          {uploadedFiles.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', fontWeight: 'medium' }}>
                {uploadedFiles.some(f => f.processing_status === 'processing' || f.processing_status === 'pending' || f.processing_status === 'chunking' || f.processing_status === 'embedding' || f.processing_status === 'unknown') ?
                  'Some files are still being processed and will appear in chat when ready. This may take a few minutes depending on file size.' :
                  'All files are ready for chat!'}
              </Typography>
              {uploadedFiles.some(f => f.processing_status === 'processing' || f.processing_status === 'pending' || f.processing_status === 'chunking' || f.processing_status === 'embedding' || f.processing_status === 'unknown') && (
                <Typography variant="body2" sx={{ mt: 1, color: 'info.main', textAlign: 'center', fontSize: '0.875rem' }}>
                  Processing includes chunking the document and creating embeddings for efficient retrieval. Please wait for processing to complete for best results.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Add URL selection modal */}
        <Modal
          open={showUrlSelectionModal}
          onClose={() => setShowUrlSelectionModal(false)}
          aria-labelledby="url-selection-modal-title"
          aria-describedby="url-selection-modal-description"
          BackdropProps={{
            style: { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
          }}
          style={{ zIndex: 9999 }}
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 1000,
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'visible',
            zIndex: 9999
          }}>
            <Typography id="url-selection-modal-title" variant="h6" component="h2" sx={{ mb: 1 }}>
              Select URLs to Crawl
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Found {flatUrls.length} URLs in sitemap. Select which pages you want to crawl.
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={selectedUrls.length === flatUrls.length}
                      indeterminate={selectedUrls.length > 0 && selectedUrls.length < flatUrls.length}
                      onChange={(e) => handleSelectAllUrls(e.target.checked)}
                    />
                  }
                  label="Select All"
                />
                <Typography variant="body2" sx={{ 
                  color: 'primary.main', 
                  fontWeight: 'medium',
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 10,
                  fontSize: '0.75rem'
                }}>
                  {selectedUrls.length} of {flatUrls.length} selected
                </Typography>
              </Box>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            {/* Info alert about crawl limits for company uploads */}
            {uploadScope !== 'personal' && userCompany && (uploadConstraints.limitTotalUploadMb || uploadConstraints.limitTotalFiles) && (
              <Alert 
                severity={(() => {
                  // Determine severity based on limits
                  if (uploadConstraints.limitTotalFiles !== null && uploadTotals.currentFileCount !== null) {
                    const remainingFiles = uploadConstraints.limitTotalFiles - uploadTotals.currentFileCount;
                    if (remainingFiles <= 0) return 'error';
                    if (selectedUrls.length > remainingFiles) return 'warning';
                  }
                  return 'info';
                })()}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  📊 Subscription Limits
                </Typography>
                
                {/* File count limit */}
                {uploadConstraints.limitTotalFiles !== null && uploadTotals.currentFileCount !== null && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Files:</strong> {uploadTotals.currentFileCount}/{uploadConstraints.limitTotalFiles} used
                    {(() => {
                      const remainingFiles = uploadConstraints.limitTotalFiles - uploadTotals.currentFileCount;
                      if (remainingFiles <= 0) {
                        return <span style={{ color: '#d32f2f', fontWeight: 'bold' }}> (⚠️ LIMIT REACHED - Cannot crawl more)</span>;
                      } else if (selectedUrls.length > remainingFiles) {
                        return <span style={{ color: '#ed6c02', fontWeight: 'bold' }}> (⚠️ Only {remainingFiles} more file(s) allowed)</span>;
                      } else {
                        return <span style={{ color: '#2e7d32' }}> ({remainingFiles} remaining)</span>;
                      }
                    })()}
                  </Typography>
                )}
                
                {/* Storage limit */}
                {uploadConstraints.limitTotalUploadMb && uploadTotals.remainingTotalUploadMb !== null && (
                  <Typography variant="body2">
                    <strong>Storage:</strong> ~{Math.floor((uploadTotals.remainingTotalUploadMb * 1024) / 150)} pages remaining 
                    ({uploadTotals.remainingTotalUploadMb.toFixed(1)} MB, ~150 KB/page avg)
                  </Typography>
                )}
                
                {/* Warning message if selection exceeds limit */}
                {uploadConstraints.limitTotalFiles !== null && uploadTotals.currentFileCount !== null && (() => {
                  const remainingFiles = uploadConstraints.limitTotalFiles - uploadTotals.currentFileCount;
                  if (remainingFiles <= 0) {
                    return (
                      <Typography variant="body2" sx={{ mt: 1, color: 'error.dark', fontWeight: 'bold' }}>
                        ⚠️ You have reached your file limit. Please delete old files before crawling new pages.
                      </Typography>
                    );
                  } else if (selectedUrls.length > remainingFiles) {
                    return (
                      <Typography variant="body2" sx={{ mt: 1, color: 'warning.dark', fontWeight: 'bold' }}>
                        ⚠️ You selected {selectedUrls.length} URLs, but only {remainingFiles} can be crawled. 
                        The rest will be automatically skipped.
                      </Typography>
                    );
                  }
                  return null;
                })()}
              </Alert>
            )}
            
            <Box sx={{ 
              flexGrow: 1, 
              overflow: 'auto', 
              py: 1,
              px: 1,
              bgcolor: '#f8f9fa', 
              borderRadius: 1,
              maxHeight: '60vh'
            }}>
              {Object.keys(sitemapUrlHierarchy).map((domain, idx) => (
                <UrlHierarchyItem key={idx} name={domain} data={sitemapUrlHierarchy[domain]} />
              ))}
            </Box>
            
            <Divider sx={{ mt: 2, mb: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => setShowUrlSelectionModal(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => {
                  setShowUrlSelectionModal(false);
                  handleInitiateSitemapCrawl();
                }}
                disabled={selectedUrls.length === 0}
                startIcon={isSitemapCrawling ? 
                  <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
              >
                Crawl {selectedUrls.length} Selected URLs
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* --- TEAM CONTEXT MODAL --- */}
        <Dialog open={showTeamContextModal} onClose={() => {setShowTeamContextModal(false); setModalError(''); setActionToPerform(null);}} maxWidth="sm" fullWidth>
          <DialogTitle>Select Context for New Content</DialogTitle>
          <DialogContent>
            {modalError && <Alert severity="error" sx={{ mb: 2 }}>{modalError}</Alert>}
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend" sx={{mb:1}}>Associate this content with:</FormLabel>
              <RadioGroup 
                aria-label="upload-scope" 
                name="uploadScope" 
                value={uploadScope} 
                onChange={(e) => {
                  setUploadScope(e.target.value); 
                  setSelectedTeamId(''); 
                  setModalError(''); 
                  setShowCreateTeamInModalForm(false);
                }}
              >
                <FormControlLabel value="personal" control={<Radio />} label="Personal Use (only you can access)" />
                {userCompany && (
                  <>
                    <FormControlLabel value="company" control={<Radio />} label={`Company: ${userCompany.name} (company-wide)`} />
                    <Box sx={{ pl: 4, mb: 1, mt:1 }}>
                      <FormControlLabel 
                        value="team" 
                        control={<Radio />} 
                        label="A specific Team:" 
                        disabled={!userCompany} // Radio for "Team" scope only disabled if no company context
                      />
                      {uploadScope === 'team' && userCompany && ( // Show team selection/creation only if "team" scope selected and company exists
                        <Box sx={{ ml: 4, mt: 1, mb:1 }}>
                          <FormControl fullWidth disabled={showCreateTeamInModalForm || loadingTeams}>
                            <InputLabel id="select-team-label">Select Team</InputLabel>
                            <Select
                              labelId="select-team-label"
                              value={selectedTeamId}
                              label="Select Team"
                              onChange={(e) => setSelectedTeamId(e.target.value)}
                              size="small"
                            >
                              {loadingTeams && <MenuItem value="" disabled><CircularProgress size={20} sx={{mr:1}} /> Loading teams...</MenuItem>}
                              {!loadingTeams && companyTeams.length === 0 && 
                                  <MenuItem value="" disabled>No teams available. Create one below.</MenuItem>}
                              {companyTeams.map((team) => (
                                <MenuItem key={team.team_id} value={team.team_id}>{team.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button 
                            size="small" 
                            onClick={() => setShowCreateTeamInModalForm(true)} 
                            sx={{ mt: 1, textTransform: 'none' }} 
                            disabled={showCreateTeamInModalForm || loadingCreateTeamInModal}
                          >
                            + Create New Team for {userCompany.name}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </RadioGroup>
            </FormControl>

            <Collapse in={showCreateTeamInModalForm && uploadScope === 'team' && userCompany}>
              <DialogContentText sx={{mt: 2, mb:1, fontWeight: 'medium'}}>Create a new team within "{userCompany?.name}":</DialogContentText>
              <TextField autoFocus margin="dense" label="New Team Name" type="text" fullWidth variant="outlined" size="small" value={newTeamNameInModal} onChange={(e) => setNewTeamNameInModal(e.target.value)} disabled={loadingCreateTeamInModal} />
              <TextField margin="dense" label="New Team Description (Optional)" type="text" fullWidth multiline rows={2} variant="outlined" size="small" value={newTeamDescriptionInModal} onChange={(e) => setNewTeamDescriptionInModal(e.target.value)} disabled={loadingCreateTeamInModal} />
              <Box sx={{display: 'flex', justifyContent:'flex-end', mt:1, gap: 1}}>
                  <Button onClick={() => {setShowCreateTeamInModalForm(false); setModalError('');}} disabled={loadingCreateTeamInModal}>Cancel</Button>
                  <Button onClick={handleCreateTeamInModal} variant="contained" size="small" disabled={loadingCreateTeamInModal}>
                      {loadingCreateTeamInModal ? <CircularProgress size={20}/> : "Create & Select Team"}
                  </Button>
              </Box>
            </Collapse>

          </DialogContent>
          <DialogActions sx={{p: '16px 24px'}}>
            <Button onClick={() => {setShowTeamContextModal(false); setModalError(''); setActionToPerform(null);}}>Cancel Action</Button>
            <Button onClick={handleModalConfirmScope} variant="contained">
              Confirm & Proceed
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>

      {/* Success Snackbar */}
      <Snackbar
        open={!!message}
        autoHideDuration={6000}
        onClose={() => setMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setMessage('')} 
          severity="success" 
          sx={{ width: '100%' }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setMessage('')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {message}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setError('')} 
          severity="error" 
          sx={{ width: '100%' }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setError('')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}

export default FileUpload;
